import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./GameRoom.scss";
import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import Dialog from '../components/ActionDialog';
import AppMenuBar from "../components/AppMenuBar";
import DeskDiscardCards from '../components/DeskDiscardCards';
import DeclareWinButton from '../components/DeclareWinButton';
import GameSettingsFields, { DEFAULT_MONEY_CARDS, DEFAULT_ROOM_RULES } from '../components/GameSettingsFields';
import useTurnCountdown from '../hooks/useTurnCountdown';
import ShowedCardsResultRow, { orderMatchPlayers } from '../components/ShowedCardsResultRow';
import RoomConnectionStatus from '../components/RoomConnectionStatus';
import HostJoinRequests from '../components/HostJoinRequests';
import ConfirmDialog from "../components/ConfirmDialog";
import { useNavigate, useParams } from "react-router-dom";
import { clearRoomMembership } from '../services/RoomSession';
import { useRoomSnapshot } from '../hooks/useRoomSnapshot';
import { restoreActiveRoom } from '../services/RoomRestoration';
import { createRoomReader } from '../services/RoomReader';
import { APP_BASE_DOMAIN, GAME_STATE, PLAYER_STATE, STO_PLAYER_ID, STO_PLAYER_NAME, STO_ROOM_ID, STO_VOLUME_STATUS } from "../constants/commonConstants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import WarningDialog from "../components/WarningDialog";
import { changeJoinApproval, changeName, changeSetting, declareWinner, discard, groupCards, pickDeckCard, pickDiscardedCard, readyGame, requestSwap, responseSwap, showWinningCard, startGame } from "../services/ActionService";
import PokerCard from "../components/PokerCard";
import HalfCard from "../components/HalfCard";
import DraggableCard from "../components/DraggableCard";
import { buildHandRows } from './handLayout';
import CustomDragLayer from "../components/CustomDragLayer";
import RoomInfoDialog from "../components/RoomInfoDialog";
import LowInternet from "../components/LowInternet";
import CardShuffleLoading from '../components/CardShuffleLoading';
import ExpandableText from '../components/ExpandableText';
import ChatDialog from "../components/ChatDialog";
import { sendMessage } from "../services/ChatService";
import RoomVoiceChat from "../components/RoomVoiceChat";
import { customStorage, isNullOrEmptyString } from "../services/CommonService";
import SignalRService from "../services/SignalRService";
import { useRoomExit } from "../hooks/useRoomExit";
import { useRoomPlayerRemoval } from '../hooks/useRoomPlayerRemoval';
import { createGameAudioPlayer } from '../services/GameAudioPlayer';
import { gameActionSounds } from '../services/GameActionSounds';
import { autoArrangeCards } from '../services/AutoArrangeCards';
import { appendCardGroup } from '../services/CardGrouping';
import SortIcon from '@mui/icons-material/Sort';
import { createChatNotifications } from '../services/ChatNotifications';
import { useToast } from "../components/Toast";
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import CloseIcon from '@mui/icons-material/Close';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/Check';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';

import discardCardSound from "../assets/game-sound/discard-card.mp3";
import gameStartSound from "../assets/game-sound/game-start.mp3";
import joinLeaveSound from "../assets/game-sound/join-leave-alert.mp3";
import pickCardSound from "../assets/game-sound/pick-card.mp3";
import shuffleSound from "../assets/game-sound/riffle-card-shuffle.mp3";
import showWinningSound from "../assets/game-sound/show-winning-cards.mp3";
import winDeclareSound from "../assets/game-sound/win-declare.mp3";
import readyClickSound from "../assets/game-sound/ready-click.mp3";
import playerEffectThrowSound from "../assets/player-effects/throw-swish.wav";
import playerEffectHitSound from "../assets/player-effects/hit-pop.wav";
import FeedbackDialog from '../components/FeedbackDialog';
import { useSubscription } from '../context/SubscriptionContext';
import SubscriptionDialog from '../components/SubscriptionDialog';
import SubscriptionWarning from '../components/SubscriptionWarning';
import { DEFAULT_CARD_BACK_DESIGN, getCardBackImage, preloadCardBackImage } from '../services/CardBackPreference';
import PlayerAvatar from '../components/PlayerAvatar';
import { buildRoundRobinDealSequence, DEAL_CARD_ANIMATION_MS, DEAL_CARD_INTERVAL_MS } from '../services/CardDistribution';
import ResultCollapsibleSection from '../components/ResultCollapsibleSection';
import PlayerInteractionDialog from '../components/PlayerInteractionDialog';
import PlayerEffectsLayer, { PLAYER_EFFECT_IMPACT_MS, PLAYER_EFFECT_PREPARE_MS, PLAYER_EFFECT_TOTAL_MS } from '../components/PlayerEffectsLayer';
import { getGameErrorMessage } from '../services/ApiError';

const gameAudios = {
  discardCardSound: discardCardSound,
  gameStartSound: gameStartSound,
  joinLeaveSound: joinLeaveSound,
  pickCardSound: pickCardSound,
  shuffleSound: shuffleSound,
  showWinningSound: showWinningSound,
  winDeclareSound: winDeclareSound,
  readyClickSound: readyClickSound,
  playerEffectThrowSound,
  playerEffectHitSound
}

const OPENING_PHASE = Object.freeze({ idle: 'idle', shuffling: 'shuffling', dealing: 'dealing' });
const INVITATION_TITLE = 'Join My Room and Play With Me!';
const INVITATION_TEXT = 'Join my room so we can play together. Open this link:';
const ROOM_POLLING_INTERVAL_MS = 12_000;

const TurnCountdown = React.memo(({ deadlineUtc, serverTimeUtc }) => {
  const { t } = useTranslation();
  const seconds = useTurnCountdown(deadlineUtc, serverTimeUtc);
  if (seconds === null) return null;
  return <span className="turn-countdown" aria-label={t('gameSettings.timeRemaining', { count: seconds })}>
    {seconds}s
  </span>;
});

const useTurnDeadlineExpired = (deadlineUtc, serverTimeUtc) => {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const deadline = Date.parse(deadlineUtc);
    if (!Number.isFinite(deadline)) {
      setExpired(false);
      return undefined;
    }

    const parsedServerTime = Date.parse(serverTimeUtc);
    const referenceTime = Number.isFinite(parsedServerTime) ? parsedServerTime : Date.now();
    const remaining = deadline - referenceTime;
    if (remaining <= 0) {
      setExpired(true);
      return undefined;
    }

    setExpired(false);
    const timer = setTimeout(() => setExpired(true), remaining + 1);
    return () => clearTimeout(timer);
  }, [deadlineUtc, serverTimeUtc]);
  return expired;
};

const scrollResultCardsHorizontally = (event) => {
  const scroller = event.currentTarget;
  if (scroller.scrollWidth <= scroller.clientWidth) return;

  // A regular mouse wheel has no horizontal axis. Translate its vertical
  // movement only while this row has cards outside its visible width.
  if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
    scroller.scrollLeft += event.deltaY;
    event.preventDefault();
  }
};

const startResultCardDrag = (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  const scroller = event.currentTarget;
  if (scroller.scrollWidth <= scroller.clientWidth) return;
  scroller.dataset.dragStartX = String(event.clientX);
  scroller.dataset.dragStartScrollLeft = String(scroller.scrollLeft);
  scroller.setPointerCapture(event.pointerId);
  scroller.classList.add('is-dragging');
};

const moveResultCardDrag = (event) => {
  const scroller = event.currentTarget;
  if (scroller.dataset.dragStartX === undefined) return;
  const distance = event.clientX - Number(scroller.dataset.dragStartX);
  scroller.scrollLeft = Number(scroller.dataset.dragStartScrollLeft) - distance;
  event.preventDefault();
};

const stopResultCardDrag = (event) => {
  const scroller = event.currentTarget;
  delete scroller.dataset.dragStartX;
  delete scroller.dataset.dragStartScrollLeft;
  scroller.classList.remove('is-dragging');
  if (scroller.hasPointerCapture?.(event.pointerId)) scroller.releasePointerCapture(event.pointerId);
};

const resultCardScrollProps = {
  onWheel: scrollResultCardsHorizontally,
  onPointerDown: startResultCardDrag,
  onPointerMove: moveResultCardDrag,
  onPointerUp: stopResultCardDrag,
  onPointerCancel: stopResultCardDrag
};



const GameRoom = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { roomId, playerId } = useParams();
  const { feature: subscriptionFeature } = useSubscription();
  const soundAccess = subscriptionFeature('poker', 'sounds');
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const soundAllowedRef = useRef(soundAccess.allowed);
  soundAllowedRef.current = soundAccess.allowed;
  const navigate = useNavigate();
  useEffect(() => {
    document.documentElement.classList.add('game-room-active');
    document.body.classList.add('game-room-active');
    return () => {
      document.documentElement.classList.remove('game-room-active');
      document.body.classList.remove('game-room-active');
    };
  }, []);
  const roomReaderRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const intentionalExitRef = useRef(false);
  const [connectionReady, setConnectionReady] = useState(false);
  const [connectionState, setConnectionState] = useState({ ready: false, status: 'initializing', exhausted: false });
  useEffect(() => {
    const changed = event => {
      if (!event.detail.ready) roomReaderRef.current?.cancel();
      setConnectionReady(event.detail.ready); setConnectionState(event.detail);
    };
    window.addEventListener('pwf-connection-state', changed);
    return () => window.removeEventListener('pwf-connection-state', changed);
  }, []);
  useEffect(() => {
    // Presence must not depend on the normal poll countdown: frequent hub
    // updates reset that countdown even for a player who has not taken a turn.
    const heartbeat = setInterval(() => {
      roomReaderRef.current?.read();
    }, 30000);
    return () => clearInterval(heartbeat);
  }, [roomId, playerId]);
  useEffect(() => {
    const membershipEnded = (event) => {
      if (event.detail?.roomId !== roomId) return;
      if (intentionalExitRef.current) return;
      if (Date.parse(event.detail?.autoRejoinUntilUtc || '') > Date.now()) {
        showToast({ message: t('roomMembership.autoRejoining'), variant: 'info' });
        SignalRService.retry();
        return;
      }
      clearRoomMembership(roomId);
      if (customStorage.getItem(STO_ROOM_ID) === roomId) {
        customStorage.removeItem(STO_ROOM_ID);
        customStorage.removeItem(STO_PLAYER_ID);
      }
      SignalRService.setRequired(false);
      SignalRService.leaveRoom(roomId, playerId).catch(() => {});
      setRoomData(null);
      setSelectedCardId([]);
      showToast({ message: t('roomMembership.previousRoomEnded'), variant: 'info' });
      navigate('/poker', { replace: true });
    };
    window.addEventListener('pwf-room-membership-ended', membershipEnded);
    return () => window.removeEventListener('pwf-room-membership-ended', membershipEnded);
  // setRoomData is memoized by the same room/player identity already listed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, playerId, navigate, showToast, t]);
  const [loading, setLoading] = useState(false);
  const actionInFlightRef = useRef(false);
  const pickCommittedRef = useRef(false);
  const [confirmText, setConfirmText] = useState('');
  const { roomData, setRoomData, currentDiscardedCard } = useRoomSnapshot(roomId, playerId);
  const turnDeadlineExpired = useTurnDeadlineExpired(roomData?.turnDeadlineUtc, roomData?.serverTimeUtc);
  const turnExpired = turnDeadlineExpired || roomData?.players?.find(player => player.playerId === playerId)?.turnTimedOut;
  const isRandomRoom = roomData?.roomType === 1 || !/^\d{4,12}$/.test(roomId || '');
  const [playingPlayers, setPlayingPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [ownCards, setOwnCards] = useState([]);
  const [lowerShowedCard, setLowerShowedCard] = useState(null);
  const [openingPhase, setOpeningPhase] = useState(OPENING_PHASE.idle);
  const [cardBackReady, setCardBackReady] = useState(false);
  const isOpeningShuffle = openingPhase === OPENING_PHASE.shuffling;
  const isOpeningDeal = openingPhase === OPENING_PHASE.dealing;
  const isOpeningSequence = openingPhase !== OPENING_PHASE.idle;
  const openingShuffleSequence = useRef(0);
  const openingPhaseRef = useRef(OPENING_PHASE.idle);
  const [gameState, setGameState] = useState(null);
  const canSwapPositions = roomData?.gameState === GAME_STATE.initialHosted || roomData?.gameState === GAME_STATE.readyForNextMatch;
  // const [playerState, setPlayerState] = useState(null);
  // const [playerId, setPlayerId] = useState(null);
  const playerIndex = Math.max(0, roomData?.players?.findIndex(p => p.playerId === playerId) ?? 0);
  const [activePlayerIndex, setActivePlayerIndex] = useState(null);
  const [playerInfo, setPlayerInfo] = useState(null);
  const [activePlayer, setActivePlayer] = useState(null);
  const [, setNextPlayerIndex] = useState(null);
  const [nextPlayer, setNextPlayer] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [hostPlayer, setHostPlayer] = useState(null);
  // const [winnginPlayerName, setWinningPlayerName] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const errorMessageRef = useRef('');
  const loadingSituationRef = useRef('');
  const [, setSpectatingPlayers] = useState([]);
  const [deckCardCount, setDeckCardCount] = useState(0);
  const [showPickPairCardConfirm, setShowPickPairCardConfirm] = useState(false);
  const [showConfirmDeclare, setShowConfirmDeclare] = useState(false);
  const [jokerDiscardCardId, setJokerDiscardCardId] = useState(null);
  const [showGameInfoDialog, setShowGameInfoDialog] = useState(false);
  const [removePlayer, setRemovePlayer] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [groupedCards, setGroupedCards] = useState({});
  const handCardsRef = useRef(null);
  const [useSingleHandRow, setUseSingleHandRow] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [initialDelayComplete, setInitialDelayComplete] = useState(false);
  const containerRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [message, setMessage] = useState('');
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [chatInfos, setChatInfos] = useState([]);
  const [playersForChat, setPlayersForChat] = useState([]);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const chatNotifications = useRef(createChatNotifications());
  useEffect(() => {
    chatNotifications.current.reset();
    setNewMessageCount(0);
  }, [roomId, playerId]);
  const audioRef = useRef(null);
  const playAudioRef = useRef(() => Promise.resolve(false));
  const [isSoundOn, setIsSoundOn] = useState(() => customStorage.persistent.getItem(STO_VOLUME_STATUS) === 'true');
  const soundOnRef = useRef(false);
  soundOnRef.current = isSoundOn;
  const soundSnapshotRef = useRef(null);
  const actionSoundSnapshotRef = useRef(null);
  const roomActionSoundHandlerRef = useRef(null);
  const playedRoomSoundsRef = useRef(new Map());
  const pendingLocalCardSoundsRef = useRef(new Map());
  const [voiceStatuses, setVoiceStatuses] = useState({});
  useEffect(() => {
    setVoiceStatuses({});
    const updateVoiceStatuses = event => {
      const statuses = Array.isArray(event.detail) ? event.detail : [];
      setVoiceStatuses(Object.fromEntries(statuses.map(status => [status.playerId, status])));
    };
    window.addEventListener('pwf-voice-statuses', updateVoiceStatuses);
    return () => window.removeEventListener('pwf-voice-statuses', updateVoiceStatuses);
  }, [roomId]);
  const [showAutoArrangeConfirm, setShowAutoArrangeConfirm] = useState(false);
  const [showSettingDialog, setShowSettingDialog] = useState(false);
  const [showJoinApprovalDialog, setShowJoinApprovalDialog] = useState(false);
  const [joinApprovalValue, setJoinApprovalValue] = useState(true);
  const [settingDeckCount, setSettingDeckCount] = useState("");
  const [settingJokerPairs, setSettingJokerPairs] = useState("");
  const [settingCardsPerPlayer, setSettingCardsPerPlayer] = useState(13);
  const [settingMoneyCards, setSettingMoneyCards] = useState({ ...DEFAULT_MONEY_CARDS });
  const [settingMaxWaitSeconds, setSettingMaxWaitSeconds] = useState(null);
  const [settingCardBackDesign, setSettingCardBackDesign] = useState(DEFAULT_CARD_BACK_DESIGN);
  const [settingRoomRules, setSettingRoomRules] = useState({ ...DEFAULT_ROOM_RULES });
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showPlayerNameDialog, setShowPlayerNameDialog] = useState(false);
  const [tempPlayerName, setTempPlayerName] = useState("");
  const [interactionPlayer, setInteractionPlayer] = useState(null);
  const [activePlayerEffects, setActivePlayerEffects] = useState([]);
  const playerEffectTimers = useRef(new Set());

  const swapRequestsRef = useRef([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [showSwapPositionsDialog, setShowSwapPositionsDialog] = useState(false);

  const maxPlayerNameLength = 8;

  const isSpectating = (playerState) => {
    const isNotPlaying = [
      PLAYER_STATE.leftRoom,
      PLAYER_STATE.dropped,
      PLAYER_STATE.spectating,
    ].includes(playerState);
    return isNotPlaying;
  };

  const isPlayingGame = (playPlayers, index) => {
    const playerState = playPlayers[index]?.playerState;
    const isPlaying = [
      PLAYER_STATE.turnPick,
      PLAYER_STATE.pickedDeckCard,
      PLAYER_STATE.pickedDiscardedCard,
      PLAYER_STATE.waitingTurn,
      PLAYER_STATE.declaredWin,
      PLAYER_STATE.waitWinCardsShow
    ].includes(playerState);
    return isPlaying;
  };

  const errorsAndWarnsMsg = {
    noInternet: t('gameRoom.errors.noInternet'),
    invitePlayers: t('gameRoom.errors.invitePlayers'),
    noCardToPickFromDeck: t('gameRoom.errors.noCardToPickFromDeck'),
    noCardToPickFromDiscardedCard: t('gameRoom.errors.noCardToPickFromDiscardedCard'),
    selectCardToDiscard: t('gameRoom.errors.selectCardToDiscard'),
    selectOneOrPairCard: t('gameRoom.errors.selectOneOrPairCard'),
    selectCardToDeclare: t('gameRoom.errors.selectCardToDeclare'),
    cannotDiscardPickedCard: t('gameRoom.errors.cannotDiscardPickedCard'),
    cannotDiscardNextPlayerPickCard: t('gameRoom.errors.cannotDiscardNextPlayerPickCard'),
    maximumPickedCards: t('gameRoom.errors.maximumPickedCards'),
    cannotPickPairWithoutWin: t('gameRoom.errors.cannotPickPairWithoutWin'),
    selectMoreThanOneCardsToGroup: t('gameRoom.errors.selectMoreThanOneCardsToGroup'),
    cannotChangeSetting: t('gameRoom.errors.cannotChangeSetting'),
    nameAlreadyExist: t('gameRoom.errors.nameAlreadyExist'),
    roomNotFound: t('roomMembership.previousRoomEnded'),
    cannotSwapWilePlaying: t('gameRoom.errors.cannotSwapWhilePlaying'),
  }

  const localizedGameError = useCallback((error, fallback = errorsAndWarnsMsg.noInternet) =>
    getGameErrorMessage(error, t, fallback), [errorsAndWarnsMsg.noInternet, t]);



  // Store i18n keys (+params) so the displayed message is translated at render
  // time and always reflects the current language (no stale value on toggle).
  const msgInfos = {
    startTheGame: { key: 'gameRoom.info.startTheGame' },
    waitHostStart: { key: 'gameRoom.info.waitHostStart' },
    clickReady: { key: 'gameRoom.info.clickReady' },
    turnToPick: { key: 'gameRoom.info.turnToPick' },
    turnToDiscardOrDeclareWin: {
      key: playerInfo?.pickedCards?.length >= 4
        ? 'gameRoom.info.turnToDiscardMaxPicked'
        : playerInfo?.mustDeclareWinner
          ? 'gameRoom.info.turnToDiscardMustDeclare'
          : 'gameRoom.info.turnToDiscardOrDeclareWin',
    },
    waitOtherPlayerTurn: {
      key: 'gameRoom.info.waitOtherPlayerTurn',
      params: {
        playerName: activePlayer?.playerName,
        turnAction: activePlayer?.playerState === PLAYER_STATE.turnPick
          ? t('gameRoom.info.turnActionPick')
          : (activePlayer?.playerState === PLAYER_STATE.pickedDeckCard || activePlayer?.playerState === PLAYER_STATE.pickedDiscardedCard)
            ? t('gameRoom.info.turnActionDiscard')
            : '',
      },
    },
    showWinCard: { key: 'gameRoom.info.showWinCard' },
    waitShowWinCard: { key: 'gameRoom.info.waitShowWinCard', params: { playerName: activePlayer?.playerName } },
    waitOtherPlayerReady: { key: 'gameRoom.info.waitOtherPlayerReady' },
    waitForNextMatch: { key: 'gameRoom.info.waitForNextMatch' },
  }

  const gameMessage = (() => {
    const playerState = playerInfo?.playerState;
    if (gameState === GAME_STATE.initialHosted) return isHost ? msgInfos.startTheGame : msgInfos.waitHostStart;
    if (gameState === GAME_STATE.playing) {
      if (playerState === PLAYER_STATE.turnPick) return msgInfos.turnToPick;
      if (playerState === PLAYER_STATE.pickedDeckCard || playerState === PLAYER_STATE.pickedDiscardedCard)
        return msgInfos.turnToDiscardOrDeclareWin;
      if (playerState === PLAYER_STATE.waitingTurn) return msgInfos.waitOtherPlayerTurn;
      if (isSpectating(playerState)) return msgInfos.waitForNextMatch;
    }
    if (gameState === GAME_STATE.winDeclared) {
      if (playerState === PLAYER_STATE.declaredWin) return msgInfos.showWinCard;
      if (playerState === PLAYER_STATE.waitWinCardsShow) return msgInfos.waitShowWinCard;
    }
    if (gameState === GAME_STATE.readyForNextMatch) {
      if (playerState === PLAYER_STATE.notReady) return msgInfos.clickReady;
      if (playerState === PLAYER_STATE.ready) return msgInfos.waitOtherPlayerReady;
    }
    return '';
  })();
  const isPickedDeskCardMove = activePlayer?.playerState === PLAYER_STATE.pickedDeckCard;

  const fetchRoomData = async () => {
    return roomReaderRef.current?.read();
  };

  const resetPollingCount = useCallback(() => {
    clearTimeout(pollingTimerRef.current);
    pollingTimerRef.current = setTimeout(() => {
      pollingTimerRef.current = null;
      if (isNullOrEmptyString(errorMessageRef.current) && isNullOrEmptyString(loadingSituationRef.current)) {
        void roomReaderRef.current?.read();
      }
      resetPollingCount();
    }, ROOM_POLLING_INTERVAL_MS);
  }, []);
  const roomEventCallbacksRef = useRef(null);
  roomEventCallbacksRef.current = { resetPollingCount, setRoomData, showToast, t };

  useEffect(() => {
    const reader = createRoomReader({
      roomId, playerId,
      canRead: () => Boolean(roomId && playerId) && SignalRService.isReady() && document.visibilityState !== 'hidden',
      onStart: resetPollingCount,
      onSnapshot: setRoomData,
      onError: error => {
        const status = error?.response?.status;
        if (!status || status === 408 || status === 429 || status >= 500) {
          // An outage is not a room error or logout. The coordinator pauses
          // ordinary polling until authority/state/subscription recover.
          SignalRService.requestRecovery();
        } else if (status === 400) setErrorMessage(localizedGameError(error));
      },
    });
    roomReaderRef.current = reader;
    resetPollingCount();
    const hidden = () => { if (document.visibilityState === 'hidden') reader.cancel(); };
    const pageHidden = () => reader.cancel();
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', pageHidden);
    return () => {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
      reader.dispose();
      if (roomReaderRef.current === reader) roomReaderRef.current = null;
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', pageHidden);
    };
  }, [localizedGameError, roomId, playerId, resetPollingCount, setRoomData]);

  useEffect(() => {
    const openingSequenceTracker = openingShuffleSequence;
    const effectTimers = playerEffectTimers.current;
    let disposed = false;
    setIsFirstLoad(true);
    setInitialDelayComplete(false);
    // The global connection dialog is relevant only while gameplay requires
    // the room hub. Initializing the socket is not itself a connection loss.
    SignalRService.setRequired(true);
    audioRef.current = createGameAudioPlayer(Object.values(gameAudios));
    audioRef.current.preload();

    const enableAudioContext = () => {
      // The player primes each mobile HTMLAudio clip only once. Calling this
      // on later gestures is safe and also lets a failed authorization retry.
      audioRef.current?.unlock()?.catch(() => { });
    };

    // Capture the gesture before a game control handles it. Mobile browsers
    // can otherwise start the action before their audio channel is unlocked.
    const audioGestureEvent = window.PointerEvent ? 'pointerdown' : 'touchstart';
    window.addEventListener(audioGestureEvent, enableAudioContext, true);
    window.addEventListener("keydown", enableAudioContext, true);

    const resumeVisibleAudio = () => {
      if (document.visibilityState === 'hidden' || !soundOnRef.current || !soundAllowedRef.current) return;
      audioRef.current?.resume()?.catch(() => {});
    };
    document.addEventListener('visibilitychange', resumeVisibleAudio);
    window.addEventListener('pageshow', resumeVisibleAudio);
    window.addEventListener('focus', resumeVisibleAudio);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const firstLoadTimer = setTimeout(() => {
      setInitialDelayComplete(true);
    }, 2000);

    const signalRInitialize = async () => {
      try {
        SignalRService.onJoin = async (roomData) => {
          try {
            playAudioRef.current(gameAudios.joinLeaveSound);
            roomEventCallbacksRef.current.setRoomData(roomData);
            roomEventCallbacksRef.current.resetPollingCount();
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };

        SignalRService.onLeave = async (roomData) => {
          try {
            playAudioRef.current(gameAudios.joinLeaveSound);
            roomEventCallbacksRef.current.setRoomData(roomData);
            roomEventCallbacksRef.current.resetPollingCount();
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };

        SignalRService.onStateChange = async (roomData) => {
          try {
            roomActionSoundHandlerRef.current?.(roomData);
            roomEventCallbacksRef.current.setRoomData(roomData);
            roomEventCallbacksRef.current.resetPollingCount();
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };

        SignalRService.onPlayerJoin = (player) => {
          try {
            if (player?.playerName && player?.playerId !== playerId) {
              roomEventCallbacksRef.current.showToast({
                message: roomEventCallbacksRef.current.t('gameRoom.toasts.playerJoined', { playerName: player.playerName }),
                variant: 'success',
              });
            }
          } catch (error) {
            console.error('join toast error', error);
          }
        };

        SignalRService.onPlayerLeave = (player) => {
          try {
            if (player?.playerName && player?.playerId !== playerId) {
              roomEventCallbacksRef.current.showToast({
                message: roomEventCallbacksRef.current.t('gameRoom.toasts.playerLeft', { playerName: player.playerName }),
                variant: 'info',
              });
            }
          } catch (error) { console.error('leave toast error', error); }
        };

        SignalRService.onSendMessage = async (chatInfos) => {
          try {
            setChatInfos(chatInfos);
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };
        SignalRService.onPlayerEffect = (effect) => {
          if (!effect?.eventId || effect.roomId !== roomId) return;
          const throwSoundTimer = setTimeout(() => {
            void playAudioRef.current(gameAudios.playerEffectThrowSound);
            effectTimers.delete(throwSoundTimer);
          }, PLAYER_EFFECT_PREPARE_MS);
          effectTimers.add(throwSoundTimer);
          const hitSoundTimer = setTimeout(() => {
            void playAudioRef.current(gameAudios.playerEffectHitSound);
            effectTimers.delete(hitSoundTimer);
          }, PLAYER_EFFECT_IMPACT_MS);
          effectTimers.add(hitSoundTimer);
          setActivePlayerEffects(current => current.some(item => item.eventId === effect.eventId)
            ? current : [...current, effect]);
          const timer = setTimeout(() => {
            setActivePlayerEffects(current => current.filter(item => item.eventId !== effect.eventId));
            effectTimers.delete(timer);
          }, PLAYER_EFFECT_TOTAL_MS);
          effectTimers.add(timer);
        };
        SignalRService.configureRoomRecovery(roomId, playerId, async signal => {
          const snapshot = await restoreActiveRoom(roomId, playerId, () => !disposed && SignalRService.isRequired(), signal);
          if (disposed || !snapshot) return false;
          roomEventCallbacksRef.current.setRoomData(snapshot);
          setErrorMessage('');
          roomEventCallbacksRef.current.resetPollingCount();
          return true;
        });
        await SignalRService.start();
      } catch (error) {
        // The visible-time coordinator owns retries; an outage is not Leave.
      }
    };

    signalRInitialize();

    return () => {
      disposed = true;
      openingSequenceTracker.current++;
      clearTimeout(firstLoadTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(audioGestureEvent, enableAudioContext, true);
      window.removeEventListener('keydown', enableAudioContext, true);
      document.removeEventListener('visibilitychange', resumeVisibleAudio);
      window.removeEventListener('pageshow', resumeVisibleAudio);
      window.removeEventListener('focus', resumeVisibleAudio);
      audioRef.current?.dispose();
      audioRef.current = null;
      SignalRService.onJoin = null;
      SignalRService.onLeave = null;
      SignalRService.onStateChange = null;
      SignalRService.onPlayerJoin = null;
      SignalRService.onPlayerLeave = null;
      SignalRService.onSendMessage = null;
      SignalRService.onPlayerEffect = null;
      effectTimers.forEach(clearTimeout);
      effectTimers.clear();
      SignalRService.setRequired(false);
    };
  }, [roomId, playerId]);

  useEffect(() => {
    if (initialDelayComplete && roomData?.roomId === roomId) setIsFirstLoad(false);
  }, [initialDelayComplete, roomData, roomId]);

  const handlePlayAudio = useCallback((audioSrc, waitForEnd = false) => {
    // Let the browser decide whether playback is authorized. A component-local
    // interaction flag incorrectly blocked remote card sounds even when the
    // user had already interacted with the lobby or another page in this SPA.
    if (!audioSrc || !audioRef.current || !soundOnRef.current || !soundAllowedRef.current) return Promise.resolve(false);

    const playback = waitForEnd ? audioRef.current.playUntilEnded(audioSrc) : audioRef.current.play(audioSrc);
    return playback.then(() => true).catch((err) => { console.error("Audio playback error:", err); return false; });
  }, []);
  playAudioRef.current = handlePlayAudio;

  const handlePlayLoopAudio = useCallback((audioSrc) => {
    if (!audioSrc || !audioRef.current || !soundOnRef.current || !soundAllowedRef.current) return Promise.resolve(false);
    return audioRef.current.playLoop(audioSrc).then(() => true).catch(() => false);
  }, []);

  const playRoomSound = useCallback((snapshot, sound, waitForEnd = false) => {
    if (!sound) return Promise.resolve(false);
    const version = snapshot?.stateVersion;
    let key = null;
    if (version !== undefined && version !== null) {
      key = `${snapshot?.roomId}:${version}:${sound}`;
      const existing = playedRoomSoundsRef.current.get(key);
      if (existing) return existing === true ? Promise.resolve(false) : existing;
    }
    const pendingLocal = pendingLocalCardSoundsRef.current.get(sound);
    if (pendingLocal) pendingLocalCardSoundsRef.current.delete(sound);
    const playback = (pendingLocal?.playback || handlePlayAudio(gameAudios[sound], waitForEnd)).then(played => {
      if (key) {
        if (played) playedRoomSoundsRef.current.set(key, true);
        else playedRoomSoundsRef.current.delete(key);
      }
      return played;
    });
    if (key) {
      playedRoomSoundsRef.current.set(key, playback);
      // Retain only a small recent deduplication window for long-running rooms.
      if (playedRoomSoundsRef.current.size > 200) {
        const recent = [...playedRoomSoundsRef.current.entries()].slice(-100);
        playedRoomSoundsRef.current = new Map(recent);
      }
    }
    return playback;
  }, [handlePlayAudio]);

  const beginLocalCardSound = useCallback(sound => {
    const entry = { sound, playback: handlePlayAudio(gameAudios[sound]) };
    pendingLocalCardSoundsRef.current.set(sound, entry);
    return entry;
  }, [handlePlayAudio]);

  const cancelLocalCardSound = useCallback(entry => {
    if (entry && pendingLocalCardSoundsRef.current.get(entry.sound) === entry) {
      pendingLocalCardSoundsRef.current.delete(entry.sound);
    }
  }, []);

  const playDetectedRoomSounds = useCallback((snapshot, sounds) => {
    if (sounds.length <= 1) {
      sounds.forEach(sound => playRoomSound(snapshot, sound));
      return;
    }
    // A timeout/fast action can collapse pick + discard into one snapshot.
    // Keep the UI immediate, but serialize its cues so neither masks the other.
    void sounds.reduce(
      (previousCue, sound) => previousCue.then(() => playRoomSound(snapshot, sound, true)),
      Promise.resolve()
    );
  }, [playRoomSound]);

  roomActionSoundHandlerRef.current = snapshot => {
    const previous = actionSoundSnapshotRef.current;
    const sameRoom = previous?.roomId === snapshot?.roomId;
    const previousVersion = Number(previous?.stateVersion ?? -1);
    const nextVersion = Number(snapshot?.stateVersion ?? -1);
    if (sameRoom && nextVersion <= previousVersion) return;
    actionSoundSnapshotRef.current = snapshot;
    const actionSounds = gameActionSounds(previous, snapshot)
      .filter(sound => sound === 'pickCardSound' || sound === 'discardCardSound');
    playDetectedRoomSounds(snapshot, actionSounds);
  };

  useEffect(() => {
    if (!isSoundOn || !soundAccess.allowed) audioRef.current?.stop();
  }, [isSoundOn, soundAccess.allowed]);

  useEffect(() => {
    let active = true;
    setCardBackReady(false);
    preloadCardBackImage(roomData?.cardBackDesign).then(() => {
      if (active) setCardBackReady(true);
    });
    return () => { active = false; };
  }, [roomData?.cardBackDesign]);

  useEffect(() => {
    const previous = soundSnapshotRef.current;
    const sounds = gameActionSounds(previous, roomData);
    soundSnapshotRef.current = roomData;
    const actionBaseline = actionSoundSnapshotRef.current;
    if (!actionBaseline || actionBaseline.roomId !== roomData?.roomId ||
      Number(roomData?.stateVersion ?? -1) > Number(actionBaseline.stateVersion ?? -1)) {
      actionSoundSnapshotRef.current = roomData;
    }
    if (roomData?.gameState !== GAME_STATE.playing && openingPhaseRef.current !== OPENING_PHASE.idle) {
      ++openingShuffleSequence.current;
      openingPhaseRef.current = OPENING_PHASE.idle;
      setOpeningPhase(OPENING_PHASE.idle);
      audioRef.current?.stop();
    }
    const matchStarted = previous?.roomId === roomData?.roomId &&
      previous?.gameState !== GAME_STATE.playing && roomData?.gameState === GAME_STATE.playing;
    if (!matchStarted) {
      playDetectedRoomSounds(roomData, sounds);
      return;
    }

    const sequence = ++openingShuffleSequence.current;
    openingPhaseRef.current = OPENING_PHASE.shuffling;
    setOpeningPhase(OPENING_PHASE.shuffling);
    const minimumAnimation = new Promise(resolve => setTimeout(resolve, 1600));
    // A failed/cached audio or image event must never hold the table in the
    // shuffle phase forever. Continue dealing after this safety deadline.
    const maximumShuffleWait = new Promise(resolve => setTimeout(resolve, 4500));
    const cardBackReady = preloadCardBackImage(roomData?.cardBackDesign);
    if (sounds.includes('shuffleSound')) void handlePlayLoopAudio(gameAudios.shuffleSound);
    const openingSounds = sounds.filter(sound => sound !== 'shuffleSound').map(sound => playRoomSound(roomData, sound, true));
    Promise.race([
      Promise.allSettled([minimumAnimation, cardBackReady, ...openingSounds]),
      maximumShuffleWait,
    ])
      .then(() => {
        if (openingShuffleSequence.current !== sequence) return;
        // The shuffle loop belongs only to the shuffle phase. Each visual deal
        // gets its own pick-card cue after the shuffle audio has stopped.
        audioRef.current?.stop();
        openingPhaseRef.current = OPENING_PHASE.dealing;
        setOpeningPhase(OPENING_PHASE.dealing);
        const openingPlayerCount = roomData?.players?.filter(player => !isSpectating(player.playerState)).length || 1;
        const totalCards = (roomData?.cardsPerPlayer || 13) * openingPlayerCount;
        const openingDealDuration = Math.max(0, totalCards - 1) * DEAL_CARD_INTERVAL_MS + DEAL_CARD_ANIMATION_MS + 120;
        const playNextDealSound = cardIndex => {
          if (openingShuffleSequence.current !== sequence || cardIndex >= totalCards) return;
          void handlePlayAudio(gameAudios.pickCardSound);
          if (cardIndex + 1 < totalCards) {
            setTimeout(() => playNextDealSound(cardIndex + 1), DEAL_CARD_INTERVAL_MS);
          }
        };
        playNextDealSound(0);
        setTimeout(() => {
          if (openingShuffleSequence.current === sequence) {
            openingPhaseRef.current = OPENING_PHASE.idle;
            setOpeningPhase(OPENING_PHASE.idle);
          }
        }, openingDealDuration);
      });
  }, [roomData, handlePlayAudio, handlePlayLoopAudio, playDetectedRoomSounds, playRoomSound]);

  useEffect(() => {
    errorMessageRef.current = errorMessage;
  }, [errorMessage]);
  useEffect(() => {
    loadingSituationRef.current = loading ? 'Loading' : '';
  }, [loading])

  useEffect(() => {
    const allPlrs = roomData?.players || [];
    const spectators = allPlrs.filter(p => p.playerState === PLAYER_STATE.spectating);
    let playingPlrs = allPlrs?.filter(p => !isSpectating(p.playerState)) || [];
    playingPlrs = playingPlrs ? [...playingPlrs?.slice(playerIndex), ...playingPlrs?.slice(0, playerIndex)] : [];
    // const playingPlrs = roomData?.players?.filter(p => p.playerState !== PLAYER_STATE.spectating);
    const reArrangedPlayers = allPlrs ? [...allPlrs?.slice(playerIndex), ...allPlrs?.slice(0, playerIndex)] : [];
    const pInfo = allPlrs?.find(p => p.playerId === playerId);
    const activePlr = reArrangedPlayers?.find(p => p.playerState === PLAYER_STATE.turnPick || p.playerState === PLAYER_STATE.pickedDeckCard
      || p.playerState === PLAYER_STATE.pickedDiscardedCard || p.playerState === PLAYER_STATE.declaredWin);
    const activePlrIndex = reArrangedPlayers?.findIndex(p => p.playerId === activePlr?.playerId);
    const gState = roomData?.gameState;
    const ownPcards = roomData?.ownCards;
    setAllPlayers(reArrangedPlayers);
    setSpectatingPlayers(spectators);
    setActivePlayerIndex(activePlrIndex);
    setActivePlayer(activePlr);
    const hostPlr = allPlrs.find(p => p.playerId === roomData?.hostPlayerId) || allPlrs.find(p => p.isJoined !== false);
    setHostPlayer(hostPlr)
    setIsHost(hostPlr?.playerId === playerId);
    setPlayingPlayers(playingPlrs);
    setPlayerInfo(pInfo);
    setOwnCards(prev => (reorderObjectArray(prev, ownPcards, 'cardId')));
    setLowerShowedCard(roomData?.lowerShowedCard);
    setGameState(gState);
    if (activePlrIndex === -1) {
      setNextPlayerIndex(-1);
      setNextPlayer(null);
    } else {
      const nextPlrIndex = reArrangedPlayers
        .map((_, i) => (activePlrIndex + i + 1) % reArrangedPlayers.length)
        .find(index => isPlayingGame(reArrangedPlayers, index));
      setNextPlayerIndex(nextPlrIndex);
      setNextPlayer(reArrangedPlayers[nextPlrIndex]);
    }
    setDeckCardCount(roomData?.deckCardCount);
    if (roomData?.roomId === roomId) chatNotifications.current.initialize(roomData.chatInfos || []);
    setChatInfos(roomData?.chatInfos || []);
    const plrsForChat = allPlrs?.map(p => ({
      playerId: p.playerId,
      playerName: p.playerName,
      plyerState: p.playerState
    }));
    setPlayersForChat(plrsForChat);
    // const swapReqs = roomData?.swapReqs?.map(sr=> {
    //   return {
    //     ...sr,

    //   }
    // })
    const relatedSwapRequests = roomData?.swapRequests?.filter(sr => sr.requestPlayerId === playerId || sr.targetPlayerId === playerId);
    setSwapRequests(relatedSwapRequests || []);
  }, [roomData, playerId, playerIndex, roomId]);

  useEffect(() => {
    const oldToResponseRequests = swapRequestsRef.current?.filter(sr => sr.targetPlayerId === playerId) || [];
    const newToResponseRequests = swapRequests?.filter(sr => sr.targetPlayerId === playerId) || [];
    if (!canSwapPositions) setShowSwapPositionsDialog(false);
    else if (newToResponseRequests?.length > oldToResponseRequests?.length) {
      setShowSwapPositionsDialog(true);
    }
    swapRequestsRef.current = swapRequests;
  }, [swapRequests, canSwapPositions, playerId]);


  const reorderObjectArray = (oldArray, newArray, key) => {

    oldArray = oldArray ?? [];
    newArray = newArray ?? [];

    // Preserve the order of elements in the original array that are present in the new array
    const preservedOrder = oldArray?.filter(originalItem =>
      newArray.some(newItem => newItem[key] === originalItem[key])
    );

    // Add new elements that are in the new array but not in the original array
    const addedItems = newArray?.filter(newItem =>
      !preservedOrder.some(preservedItem => preservedItem[key] === newItem[key])
    );

    // Merge the arrays
    return [...preservedOrder, ...addedItems];
  };

  const moveCard = useCallback(async (dragCardId, hoverCardId) => {
    setOwnCards((prev) => {
      const updatedCards = [...prev];
      const actualDragIndex = updatedCards.findIndex(c => c.cardId === dragCardId);
      const actualHoverIndex = updatedCards.findIndex(c => c.cardId === hoverCardId);

      if (actualDragIndex === -1 || actualHoverIndex === -1) {
        return prev;
      }

      const [draggedCard] = updatedCards.splice(actualDragIndex, 1);

      updatedCards.splice(actualHoverIndex, 0, draggedCard);
      const sortedUpdatedGroup = updatedCards?.sort((a, b) => b.group - a.group);

      return sortedUpdatedGroup;
    });
  }, []);

  useEffect(() => {
    const newGroupedCards = ownCards.reduce((acc, card) => {
      if (!acc[card?.group]) acc[card?.group] = [];
      acc[card?.group].push(card);
      return acc;
    }, {});
    let sortedGroup = Object.values(newGroupedCards)?.sort((a, b) => b[0]?.group - a[0]?.group);
    sortedGroup = [...sortedGroup, []]; // To smooth dragging for last group card!
    setGroupedCards(sortedGroup);
  }, [ownCards]);

  // Seat coordinates are CSS custom properties so responsive table layouts and
  // the card-movement animations always use the same destination.
  const playerPositions = [
    { top: 'var(--seat-0-top)', left: 'var(--seat-0-left)', slot: 0 },
    { top: 'var(--seat-1-top)', left: 'var(--seat-1-left)', slot: 1 },
    { top: 'var(--seat-2-top)', left: 'var(--seat-2-left)', slot: 2 },
    { top: 'var(--seat-3-top)', left: 'var(--seat-3-left)', slot: 3 },
    { top: 'var(--seat-4-top)', left: 'var(--seat-4-left)', slot: 4 },
    { top: 'var(--seat-5-top)', left: 'var(--seat-5-left)', slot: 5 }
  ];

  function getAllPositionedPlayers(playerCount) {
    const predefinedPatterns = [
      [0],
      [0, 3],
      [0, 2, 4],
      [0, 2, 3, 4],
      [0, 1, 2, 4, 5],
      [0, 1, 2, 3, 4, 5],
    ];

    if (playerCount <= 0 || playerCount > predefinedPatterns?.length) {
      return [];
    }

    return predefinedPatterns[playerCount - 1]?.map(index => playerPositions[index]);
  }

  const renderPickedCard = (pickedCards) => {

    return (
      pickedCards.map((card, index) => {
        // const angle = (index - (numCards - 1) / 2) * 15;
        // const translateX = (index - (numCards - 1) / 2) * 20;

        // const angle = (index - (numCards - 1) / 2) * -15;
        // const translateX = (numCards - 1 - index - (numCards - 1) / 2) * 20;

        // Progress cards from left to right and stack later cards above earlier
        // ones. The exposed left edge always contains the rank and suit.
        return (
          <PokerCard key={card.cardId} card={card} wrapperClass="card picked-card" isShowType={false} />
        );
      })
    );
  };

  const handleHorizontalCardScroll = (event) => {
    const container = event.currentTarget;
    if (container.scrollWidth <= container.clientWidth) return;

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      container.scrollLeft += event.deltaY;
      event.preventDefault();
    }
  };

  const beginGameAction = () => {
    if (actionInFlightRef.current) return false;
    actionInFlightRef.current = true;
    return true;
  };
  const endGameAction = () => { actionInFlightRef.current = false; };

  useEffect(() => {
    // Keep a successful pick locked until React has applied the authoritative
    // post-pick phase. This absorbs delayed click/touch events from the card.
    if (playerInfo?.playerState !== PLAYER_STATE.turnPick) pickCommittedRef.current = false;
  }, [playerInfo?.playerState]);

  const handlePickError = async (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.code || error?.response?.data?.message;
    if (status === 409 || code === 'TURN_PHASE_CHANGED') {
      await fetchRoomData();
      return;
    }
    pickCommittedRef.current = false;
    setErrorMessage(status === 400
      ? localizedGameError(error)
      : errorsAndWarnsMsg.noInternet);
  };

  const isPlayerTurn = (pInfo) => {
    return (gameState === GAME_STATE.playing &&
      (pInfo?.playerState === PLAYER_STATE.turnPick || pInfo?.playerState === PLAYER_STATE.pickedDeckCard || pInfo?.playerState === PLAYER_STATE.pickedDiscardedCard)) ||
      (gameState === GAME_STATE.winDeclared && pInfo.playerState === PLAYER_STATE.declaredWin)
  }

  // RoomActionFilter broadcasts successful HTTP mutations. These handlers
  // apply the response locally; a second hub notification can race a reconnect.
  const handlePickDeckCard = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current || pickCommittedRef.current) return;
    if (gameState !== GAME_STATE.playing || playerInfo?.playerState !== PLAYER_STATE.turnPick) return;

    if (deckCardCount <= 0) {
      setErrorMessage(errorsAndWarnsMsg.noCardToPickFromDeck);
      return;
    }
    if (!beginGameAction()) return;
    pickCommittedRef.current = true;
    const localSound = beginLocalCardSound('pickCardSound');

    try {
      const response = await pickDeckCard(roomId, playerId, ownCards);
      if (response) {
        void playRoomSound(response, 'pickCardSound');
        setRoomData(response);
        resetPollingCount();
      } else {
        cancelLocalCardSound(localSound);
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      cancelLocalCardSound(localSound);
      await handlePickError(err);
    } finally {
      setShowConfirmDeclare(false);
      setShowPickPairCardConfirm(false)
      endGameAction();
    }
  }

  const handlePickDiscardedCard = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current || pickCommittedRef.current) return;
    if (gameState !== GAME_STATE.playing || playerInfo?.playerState !== PLAYER_STATE.turnPick) return;

    if (!currentDiscardedCard) {
      setErrorMessage(errorsAndWarnsMsg.noCardToPickFromDiscardedCard);
      return;
    }

    if ((playerInfo?.pickedCards?.length || 0) >= 4) {
      setErrorMessage(errorsAndWarnsMsg.maximumPickedCards);
      return;
    }

    if (roomData.isDiscardedPairCards) {
      setShowPickPairCardConfirm(true);
      return;
    }

    if (playerInfo?.pickedCards?.length >= 3) {
      setShowConfirmDeclare(true);
      return;
    }

    await executeDiscardedCardPick();
  }

  const executeDiscardedCardPick = async (mustDeclareWinner = false) => {
    if (!SignalRService.isReady() || turnExpired || pickCommittedRef.current || actionInFlightRef.current) return;
    if (!currentDiscardedCard) {
      setErrorMessage(errorsAndWarnsMsg.noCardToPickFromDiscardedCard);
      return;
    }
    if ((playerInfo?.pickedCards?.length || 0) >= 4) {
      setErrorMessage(errorsAndWarnsMsg.maximumPickedCards);
      return;
    }
    if (roomData?.isDiscardedPairCards && !mustDeclareWinner) {
      setErrorMessage(errorsAndWarnsMsg.cannotPickPairWithoutWin);
      return;
    }
    if (!beginGameAction()) return;
    pickCommittedRef.current = true;
    const localSound = beginLocalCardSound('pickCardSound');
    try {
      const response = await pickDiscardedCard(roomId, playerId, mustDeclareWinner, ownCards);
      if (response) {
        void playRoomSound(response, 'pickCardSound');
        setRoomData(response);
        resetPollingCount();
      } else {
        cancelLocalCardSound(localSound);
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      cancelLocalCardSound(localSound);
      await handlePickError(err);
    } finally {
      setShowConfirmDeclare(false);
      setShowPickPairCardConfirm(false);
      endGameAction();
    }
  }

  const handleCardSelect = (cardId) => {
    setSelectedCardId(prev =>
      prev.includes(cardId)
        ? prev.filter(cId => cId !== cardId) // Remove cardId if already selected
        : [...prev, cardId] // Add cardId if not selected
    );
  }

  const handleDiscardClick = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current) return;
    const toDiscardCard = ownCards?.find(c => c.cardId === selectedCardId[0]);

    if (playerInfo?.mustDeclareWinner) {
      setErrorMessage(errorsAndWarnsMsg.cannotPickPairWithoutWin);
      return;
    }

    if (!toDiscardCard || gameState !== GAME_STATE.playing || (playerInfo?.playerState !== PLAYER_STATE.pickedDeckCard && playerInfo?.playerState !== PLAYER_STATE.pickedDiscardedCard)) {
      setErrorMessage(errorsAndWarnsMsg.selectCardToDiscard);
      return;
    }
    if (!selectedCardId || selectedCardId.length === 0) {
      setErrorMessage(errorsAndWarnsMsg.selectCardToDiscard);
      return;
    }

    if ((roomData?.protectPickedDiscardCards ?? true) && playerInfo?.pickedCards?.find(c => c.cardId === selectedCardId[0])) {
      setErrorMessage(errorsAndWarnsMsg.cannotDiscardPickedCard);
      return;
    }

    if ((roomData?.jokerDiscardRequiresWin ?? true) && selectedCardId.some(id => ownCards.find(card => card.cardId === id)?.type === 'jokers')) {
      if (selectedCardId.length !== 1) {
        setErrorMessage(t('gameRoom.errors.singleJokerOnly'));
        return;
      }
      setJokerDiscardCardId(toDiscardCard.cardId);
      return;
    }

    const pairCardToDiscardNextTurn = ownCards?.find(c => c.cardId === selectedCardId[1]);
    if (pairCardToDiscardNextTurn) {
      if (selectedCardId.length !== 2 || toDiscardCard.rank !== pairCardToDiscardNextTurn.rank || toDiscardCard.type !== pairCardToDiscardNextTurn.type) {
        setErrorMessage(errorsAndWarnsMsg.selectOneOrPairCard);
        return;
      }

      if ((roomData?.protectPickedDiscardCards ?? true) && playerInfo?.pickedCards?.find(c => c.cardId === selectedCardId[1])) {
        setErrorMessage(errorsAndWarnsMsg.cannotDiscardPickedCard);
        return;
      }
    } else {
      if (selectedCardId.length !== 1) {
        setErrorMessage(errorsAndWarnsMsg.selectOneOrPairCard);
        return;
      }
    }

    if ((roomData?.protectNextPlayerPickedRank ?? true) && nextPlayer?.pickedCards?.find(c => c.rank === toDiscardCard.rank) && !(nextPlayer?.discardedCards?.find(c => c.rank === toDiscardCard.rank))) {
      setErrorMessage(errorsAndWarnsMsg.cannotDiscardNextPlayerPickCard);
      return;
    }

    if (!beginGameAction()) return;
    const localSound = beginLocalCardSound('discardCardSound');

    try {
      const response = await discard(roomId, playerId, selectedCardId[0], ownCards, selectedCardId[1]);
      if (response) {
        void playRoomSound(response, 'discardCardSound');
        setRoomData(response);
        setSelectedCardId([]);
        setShowConfirmDeclare(false);
        resetPollingCount();
      } else {
        cancelLocalCardSound(localSound);
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      cancelLocalCardSound(localSound);
      if (err?.response?.status === 400) {
        setErrorMessage(localizedGameError(err));
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  };

  const handleDeclareClick = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current) return;
    if (gameState !== GAME_STATE.playing || (playerInfo?.playerState !== PLAYER_STATE.pickedDeckCard && playerInfo?.playerState !== PLAYER_STATE.pickedDiscardedCard)) return;
    const toDiscardCard = ownCards?.find(c => c.cardId === selectedCardId[0]);
    if (!toDiscardCard || !selectedCardId || selectedCardId.length !== 1) {
      setErrorMessage(errorsAndWarnsMsg.selectCardToDeclare)
      return;
    }
    if (playerInfo?.pickedCards?.some(card => card.cardId === toDiscardCard.cardId) || playerInfo?.nextTurnAutoDiscardCardId) {
      setErrorMessage(errorsAndWarnsMsg.cannotDiscardPickedCard);
      return;
    }
    if (!beginGameAction()) return;

    try {
      const response = await declareWinner(roomId, playerId, selectedCardId[0]);
      if (response) {
        setRoomData(response);
        setSelectedCardId([]);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err?.response?.status === 400) {
        setErrorMessage(localizedGameError(err));
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  };

  const handleShowClick = async () => {
    if (!SignalRService.isReady() || actionInFlightRef.current) return;
    if (gameState !== GAME_STATE.winDeclared || playerInfo?.playerState !== PLAYER_STATE.declaredWin || !beginGameAction()) return;

    try {
      const response = await showWinningCard(roomId, playerId, ownCards);
      if (response) {
        setRoomData(response);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err?.response?.status === 400) {
        setErrorMessage(localizedGameError(err));
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  };

  const handleInviteClick = () => {
    if (!roomData || isRandomRoom) return;
    const gamePath = roomData?.gameKey === 'shan9mee' ? 'shan9mee' : 'poker';
    const appUrl = `${(APP_BASE_DOMAIN || window.location.origin).replace(/\/+$/, '')}/${gamePath}/${roomId}`;
    const shareData = {
      title: INVITATION_TITLE,
      text: INVITATION_TEXT,
      url: appUrl,
    };

    if (navigator.share) {
      // Use Web Share API
      navigator
        .share(shareData)
        .then(() => console.log("Successfully shared"))
        .catch((error) => console.error("Error sharing:", error));
    } else {
      // Fallback logic (e.g., copy to clipboard or show URL)
      navigator.clipboard.writeText(appUrl).then(() => {
        alert(t('gameRoom.urlCopied'));
      });
    }
  };

  const handleStartGame = async () => {
    if (!SignalRService.isReady() || actionInFlightRef.current || gameState !== GAME_STATE.initialHosted) return;
    if (playingPlayers.length < 2) {
      setErrorMessage(errorsAndWarnsMsg.invitePlayers);
      return;
    }
    if (!beginGameAction()) return;

    try {
      const response = await startGame(roomId, playerId);
      if (response) {
        setRoomData(response);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err?.response?.status === 400) {
        setErrorMessage(localizedGameError(err));
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }

  }

  const handleReadyClick = async () => {
    if (!SignalRService.isReady() || actionInFlightRef.current || gameState !== GAME_STATE.readyForNextMatch) return;

    if (playingPlayers.length < 2) {
      setErrorMessage(errorsAndWarnsMsg.invitePlayers);
      return;
    }

    if (!beginGameAction()) return;

    try {
      setSelectedCardId([]);
      const response = await readyGame(roomId, playerId);
      if (response) {
        setRoomData(response);
        resetPollingCount();
        handlePlayAudio(gameAudios.readyClickSound)
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err?.response?.status === 400) {
        setErrorMessage(localizedGameError(err));
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  }

  const isMoneyUpperAndLowerCard = (card) => {
    return roomData?.showedMoneyCardIds?.includes(card?.cardId) || false;
  }

  const isMoneyCard = (card) => {
    return roomData?.moneyCardIds?.includes(card?.cardId) || false;
  }

  const { handleLeaveRoom, handleLogoutFromRoom } = useRoomExit({
    roomId, playerId, setLoading, setErrorMessage, setConfirmText,
    onRoomLeft: resetPollingCount, fallbackError: errorsAndWarnsMsg.noInternet, translateError: localizedGameError,
    lobbyPath: roomData?.gameKey === 'shan9mee' ? '/shan9mee' : '/poker',
  });

  const onRemoveClick = (player) => {
    setRemovePlayer(player);
    setShowRemoveDialog(true);
  }

  const handleRemoveClick = useRoomPlayerRemoval({
    roomId, playerId, isHost, targetPlayerId: removePlayer?.playerId,
    setLoading, setShowRemoveDialog, setErrorMessage,
    onRemoved: resetPollingCount, fallbackError: errorsAndWarnsMsg.noInternet, translateError: localizedGameError,
  });

  const handleAutoArrange = async () => {
    setShowAutoArrangeConfirm(false);
    if (loading || !SignalRService.isReady() || turnExpired || !ownCards?.length) return;
    try {
      setLoading(true);
      const arranged = autoArrangeCards(ownCards);
      const response = await groupCards(roomId, playerId, arranged);
      if (!response) { setErrorMessage(errorsAndWarnsMsg.roomNotFound); return; }
      setRoomData(response);
      setOwnCards(arranged);
      setSelectedCardId([]);
    } catch (err) {
      setErrorMessage(localizedGameError(err));
    } finally { setLoading(false); }
  };

  const handleGroupClick = async () => {
    if (!SignalRService.isReady()) return;
    try {
      setLoading(true);

      if (!selectedCardId || selectedCardId.length <= 1) {
        setErrorMessage(errorsAndWarnsMsg.selectMoreThanOneCardsToGroup);
        return;
      }

      const rearrangedCards = appendCardGroup(ownCards, selectedCardId);

      // Step 5: Submit grouped cards
      const response = await groupCards(roomId, playerId, rearrangedCards);
      if (response) {
        setRoomData(response);
        setOwnCards(rearrangedCards);
        setSelectedCardId([]);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.response?.status === 400
        ? localizedGameError(err)
        : errorsAndWarnsMsg.noInternet);
    } finally {
      setLoading(false);
    }
  };


  const isGroupOfLastCard = (card) => {
    const groupCards = ownCards?.filter(c => c.group === card.group);
    if (groupCards && groupCards.length === 1) {
      return true;
    }
    const indexOfTargetCard = groupCards?.findIndex(c => c.cardId === card.cardId);
    if (indexOfTargetCard < 0) {
      return false;
    }
    const isLast = indexOfTargetCard === groupCards?.length - 1;
    return isLast;
  }

  useEffect(() => {
    const sync = () => setIsFullScreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement || document.documentElement.classList.contains('pwf-pseudo-fullscreen')));
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      document.body.classList.remove('pwf-pseudo-fullscreen');
      document.documentElement.classList.remove('pwf-pseudo-fullscreen');
    };
  }, []);

  const enterFullscreen = async () => {
    // Fullscreen the document root so MUI menus/dialogs rendered in body
    // portals remain visible and interactive.
    const element = document.documentElement;
    const isMobileDevice = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
      window.matchMedia?.('(pointer: coarse)').matches;
    const enterPseudoFullscreen = () => {
      document.documentElement.classList.add('pwf-pseudo-fullscreen');
      document.body.classList.add('pwf-pseudo-fullscreen');
      setIsFullScreen(true);
      window.scrollTo(0, 0);
    };
    // Real browser fullscreen can suppress mobile system/browser overlays,
    // including the hardware-volume indicator on some devices. The CSS-sized
    // mode keeps the same room layout while leaving those overlays available.
    if (isMobileDevice) {
      enterPseudoFullscreen();
      return;
    }
    const request = element.requestFullscreen || element.webkitRequestFullscreen || element.mozRequestFullScreen || element.msRequestFullscreen;
    if (request) {
      try {
        await Promise.resolve(request.call(element));
        setIsFullScreen(true);
        return;
      } catch { /* iOS Safari may expose but reject the API. */ }
    }
    enterPseudoFullscreen();
  };

  const exitFullscreen = async () => {
    document.body.classList.remove('pwf-pseudo-fullscreen');
    document.documentElement.classList.remove('pwf-pseudo-fullscreen');
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
    if ((document.fullscreenElement || document.webkitFullscreenElement) && exit) {
      try { await Promise.resolve(exit.call(document)); } catch { /* State is synchronized below. */ }
    }
    setIsFullScreen(false);
  };

  const toggleFullscreen = () => isFullScreen ? exitFullscreen() : enterFullscreen();

  const handleSetMessage = (msg) => {
    setMessage(msg);
  }

  const handleSendMessage = async () => {
    if (isNullOrEmptyString(message?.trim())) {
      return;
    }
    try {
      setLoading(true);
      const response = await sendMessage(roomId, playerId, JSON.stringify(String(message)));
      if (response) {
        // The unversioned send response may predate a newer hub snapshot.
        // Read through the same revision guard as every other room update.
        await fetchRoomData();
        setMessage('');
        setNewMessageCount(0);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.response?.status === 400
        ? localizedGameError(err)
        : errorsAndWarnsMsg.noInternet);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setNewMessageCount(chatNotifications.current.observe(chatInfos, showChatDialog, playerId));
  }, [chatInfos, showChatDialog, playerId]);

  const toggleSound = () => {
    if (!soundAccess.allowed) { if (soundAccess.locked) setSubscribeOpen(true); return; }
    const nextValue = !soundOnRef.current;
    soundOnRef.current = nextValue;
    setIsSoundOn(nextValue);
    customStorage.persistent.setItem(STO_VOLUME_STATUS, String(nextValue));
    if (!nextValue) {
      audioRef.current?.stop();
      return;
    }
    // The explicit sound-button gesture unlocks production/mobile audio and a
    // short confirmation makes failure immediately visible to the player.
    const player = audioRef.current;
    void player?.unlock()?.catch(() => {});
    // Do not wait for every preloaded clip's mobile authorization promise;
    // the confirmation cue can take ownership of its own channel immediately.
    void player?.play(readyClickSound)?.catch(() => {});
  }

  const handleSaveSettings = async () => {
    if (!isHost || (gameState !== GAME_STATE.initialHosted && gameState !== GAME_STATE.readyForNextMatch)) {
      setErrorMessage(errorsAndWarnsMsg.cannotChangeSetting);
      setShowSettingDialog(false);
      return;
    }
    if (settingDeckCount === roomData?.deckCount && settingJokerPairs === roomData?.jokerPairs && settingCardsPerPlayer === (roomData?.cardsPerPlayer || 13) &&
        settingMaxWaitSeconds === (roomData?.maxWaitSeconds ?? null) && settingCardBackDesign === (roomData?.cardBackDesign || DEFAULT_CARD_BACK_DESIGN) &&
        JSON.stringify(settingMoneyCards) === JSON.stringify(roomData?.moneyCardSettings) &&
        Object.keys(DEFAULT_ROOM_RULES).filter(key => key !== 'autoApproveJoinRequests')
          .every(key => settingRoomRules[key] === (roomData?.[key] ?? DEFAULT_ROOM_RULES[key]))) {
      setShowSettingDialog(false);
      return;
    }
    try {
      setLoading(true);
      const response = await changeSetting(roomId, playerId, settingDeckCount, settingJokerPairs,
        { cardsPerPlayer: settingCardsPerPlayer, moneyCards: settingMoneyCards, maxWaitSeconds: settingMaxWaitSeconds,
          cardBackDesign: settingCardBackDesign,
          jokerDiscardRequiresWin: settingRoomRules.jokerDiscardRequiresWin,
          protectPickedDiscardCards: settingRoomRules.protectPickedDiscardCards,
          protectNextPlayerPickedRank: settingRoomRules.protectNextPlayerPickedRank });
      if (response) {
        setRoomData(response);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.response?.status === 400
        ? localizedGameError(err)
        : errorsAndWarnsMsg.noInternet);
    } finally {
      setShowSettingDialog(false);
      setLoading(false);
    }
  }

  const canEditRoomSettings = isHost && [GAME_STATE.initialHosted, GAME_STATE.readyForNextMatch].includes(gameState);
  const handleOpenSettingDialog = () => {
    setSettingDeckCount(roomData?.deckCount);
    setSettingJokerPairs(roomData?.jokerPairs);
    setSettingCardsPerPlayer(roomData?.cardsPerPlayer || 13);
    setSettingMoneyCards({ ...DEFAULT_MONEY_CARDS, ...roomData?.moneyCardSettings });
    setSettingMaxWaitSeconds(roomData?.maxWaitSeconds ?? null);
    setSettingCardBackDesign(roomData?.cardBackDesign || DEFAULT_CARD_BACK_DESIGN);
    setSettingRoomRules(Object.fromEntries(Object.keys(DEFAULT_ROOM_RULES).map(key => [key, roomData?.[key] ?? DEFAULT_ROOM_RULES[key]])));
    setShowSettingDialog(true);
  }

  const handleCancelSettings = () => {
    setShowSettingDialog(false);
  }

  const handleOpenJoinApproval = () => {
    setJoinApprovalValue(roomData?.autoApproveJoinRequests ?? true);
    setShowJoinApprovalDialog(true);
  };

  const handleSaveJoinApproval = async () => {
    try {
      setLoading(true);
      const response = await changeJoinApproval(roomId, playerId, joinApprovalValue);
      if (response) setRoomData(response);
      else setErrorMessage(errorsAndWarnsMsg.roomNotFound);
    } catch (error) {
      setErrorMessage(localizedGameError(error));
    } finally {
      setLoading(false);
      setShowJoinApprovalDialog(false);
    }
  };

  const handleChangePlayerName = async () => {
    const newName = tempPlayerName?.trim();
    const oldName = playerInfo?.playerName?.trim();
    if (isNullOrEmptyString(newName) || newName?.length > maxPlayerNameLength) {
      setTempPlayerName(newName);
      return;
    }
    if (newName === oldName) {
      setTempPlayerName("");
      setShowPlayerNameDialog(false);
    } else {
      const isNameAlreadyExist = !!(allPlayers?.find(p => p.playerName?.trim() === newName));
      if (isNameAlreadyExist) {
        setTempPlayerName("");
        setShowPlayerNameDialog(false);
        setErrorMessage(errorsAndWarnsMsg.nameAlreadyExist)
      } else {
        try {
          setLoading(true);
          const response = await changeName(roomId, playerId, newName);
          if (response) {
            setRoomData(response);
            customStorage.setItem(STO_PLAYER_NAME, newName);
          } else {
            setErrorMessage(errorsAndWarnsMsg.roomNotFound);
          }
        } catch (err) {
          setErrorMessage(err?.response?.status === 400
            ? localizedGameError(err)
            : errorsAndWarnsMsg.noInternet);
        } finally {
          setLoading(false);
          setTempPlayerName("");
          setShowPlayerNameDialog(false);
        }
      }
    }
  }

  const handleRequestSwap = async (targetPlayerId) => {
    if (!canSwapPositions) return;
    try {
      setLoading(true);
      const response = await requestSwap(roomId, playerId, targetPlayerId);
      if (response) {
        setRoomData(response);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.response?.status === 400
        ? localizedGameError(err)
        : errorsAndWarnsMsg.noInternet);
    } finally {
      setLoading(false);
    }
  }

  const handleResponseSwap = async (requestPlayerId, isAccept = false) => {
    if (!canSwapPositions) {
      setErrorMessage(errorsAndWarnsMsg.cannotSwapWilePlaying);
      setShowSwapPositionsDialog(false);
      return;
    }

    try {
      setLoading(true);
      const response = await responseSwap(roomId, playerId, requestPlayerId, isAccept);
      if (response) {
        setRoomData(response);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.response?.status === 400
        ? localizedGameError(err)
        : errorsAndWarnsMsg.noInternet);
    } finally {
      setLoading(false);
    }
  }


  const isHandVisible =
    (gameState === GAME_STATE.playing || gameState === GAME_STATE.winDeclared) &&
    playerInfo?.playerState !== PLAYER_STATE.spectating && !isOpeningSequence;
  const isResultsView = gameState === GAME_STATE.readyForNextMatch &&
    playerInfo?.playerState !== PLAYER_STATE.ready;
  const gameMessageText = gameMessage?.key ? t(gameMessage.key, gameMessage.params) : gameMessage;

  useLayoutEffect(() => {
    const container = handCardsRef.current;
    if (!isHandVisible || !container || !Array.isArray(groupedCards)) {
      setUseSingleHandRow(false);
      return undefined;
    }
    const updateLayout = () => {
      const nonEmptyGroups = groupedCards.filter(group => group.length > 0);
      const cardCount = nonEmptyGroups.reduce((total, group) => total + group.length, 0);
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      // At the normal three-rem card peek, this is the space needed to keep
      // every rank/suit legible without compressing or clipping the last card.
      const requiredWidth = (cardCount + nonEmptyGroups.length * 0.6) * 3 * rootFontSize
        + Math.max(0, nonEmptyGroups.length - 1) * 0.28 * rootFontSize + 4;
      setUseSingleHandRow(cardCount > 0 && container.clientWidth >= requiredWidth);
    };
    updateLayout();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateLayout);
      observer.observe(container);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [groupedCards, isHandVisible]);

  useLayoutEffect(() => {
    const table = containerRef.current?.querySelector('.poker-deck');
    const deckZone = table?.querySelector('.desk-deck-zone');
    if (!table || !deckZone) return;

    // The middle grid row can shift when top/bottom players have different
    // card counts. Use the actual deck center, not 50% of the whole table.
    const alignDeskCards = () => {
      const tableBounds = table.getBoundingClientRect();
      const deckBounds = deckZone.getBoundingClientRect();
      const center = `${deckBounds.top + deckBounds.height / 2 - tableBounds.top}px`;
      if (table.style.getPropertyValue('--desk-discard-top') !== center) {
        table.style.setProperty('--desk-discard-top', center);
      }
    };

    alignDeskCards();
    const observer = new ResizeObserver(alignDeskCards);
    observer.observe(table);
    observer.observe(deckZone);
    table.querySelectorAll('.opponent, .desk-card-zone').forEach(element => observer.observe(element));
    window.addEventListener('resize', alignDeskCards);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', alignDeskCards);
    };
  }, [isFirstLoad, isHandVisible, gameState, allPlayers, lowerShowedCard]);

  return isFirstLoad || !cardBackReady ?
    (<CardShuffleLoading />)
    :
    (
      <div ref={containerRef} className="game-room-page" style={{ '--card-back-image': `url("${getCardBackImage(roomData?.cardBackDesign) || ''}")` }}>
        {/* <AppMetaData /> */}
        <LowInternet isNotOnline={!isOnline} />
        <LoadingSpinner show={loading} />
        <PlayerEffectsLayer effects={activePlayerEffects} />
        <PlayerInteractionDialog open={Boolean(interactionPlayer)} player={interactionPlayer} roomId={roomId}
          onClose={() => setInteractionPlayer(null)}
          onSend={effectId => SignalRService.sendPlayerEffect(roomId, playerId, interactionPlayer.playerId, effectId)} />
        <ChatDialog open={showChatDialog} onClose={() => setShowChatDialog(false)} chatInfos={chatInfos} playerInfos={playersForChat} playerId={playerId} roomId={roomId} message={message} typeMessage={handleSetMessage} onSendClick={handleSendMessage} />
        <ConfirmDialog open={showAutoArrangeConfirm} onCancel={() => setShowAutoArrangeConfirm(false)} onHide={() => setShowAutoArrangeConfirm(false)}
          title={t('gameRoom.autoArrange')} confirmText={t('gameRoom.autoArrangeConfirm')} onConfirm={handleAutoArrange} fullWidth />
        <HostJoinRequests roomId={roomId} requests={isHost && !isRandomRoom ? roomData?.pendingJoinRequests : []} onChanged={fetchRoomData} />
        <RoomInfoDialog open={showGameInfoDialog} onClose={() => setShowGameInfoDialog(false)} roomId={roomId} isRandomRoom={isRandomRoom} deckCount={roomData?.deckCount}
          jokerPair={roomData?.jokerPairs} players={allPlayers} hostId={hostPlayer?.playerId} isHost={isHost} onRemoveClick={onRemoveClick} />
        <WarningDialog open={!!errorMessage} fullWidth={true} title={t('gameRoom.gameInformation')} warningText={errorMessage}
          onClose={() => setErrorMessage('')} onHide={() => setErrorMessage('')} />
        <ConfirmDialog open={!!confirmText} onCancel={() => setConfirmText('')} onHide={() => setConfirmText('')} onConfirm={async () => {
          intentionalExitRef.current = true;
          await handleLeaveRoom();
          intentionalExitRef.current = false;
        }} fullWidth={true} title={t('gameRoom.actionConfirmation')} confirmText={confirmText} />
        <ConfirmDialog open={showRemoveDialog} onCancel={() => setShowRemoveDialog(false)} onHide={() => setShowRemoveDialog(false)} onConfirm={handleRemoveClick} fullWidth={true} title={t('gameRoom.removeConfirmation')} confirmText={t('gameRoom.removeConfirmText', { playerName: removePlayer?.playerName })} />
        <ConfirmDialog open={showPickPairCardConfirm} onCancel={() => setShowPickPairCardConfirm(false)} onConfirm={() => executeDiscardedCardPick(true)} fullWidth={true} title={t('gameRoom.pickPairCardConfirmation')} confirmText={t('gameRoom.pickPairCardConfirmText')} />
        <ConfirmDialog open={showConfirmDeclare} onCancel={() => setShowConfirmDeclare(false)} onConfirm={() => executeDiscardedCardPick(true)} fullWidth={true} title={t('gameRoom.pickDiscardedCardConfirmation')} confirmText={t('gameRoom.pickDiscardedCardConfirmText')} />
        <ConfirmDialog open={!!jokerDiscardCardId} onCancel={() => setJokerDiscardCardId(null)} onHide={() => setJokerDiscardCardId(null)}
          title={t('gameRoom.jokerDiscardConfirmation')} confirmText={t('gameRoom.jokerDiscardConfirmText')} fullWidth
          onConfirm={() => {
            const cardId = jokerDiscardCardId;
            setJokerDiscardCardId(null);
            if (selectedCardId.length === 1 && selectedCardId[0] === cardId && ownCards.find(card => card.cardId === cardId)?.type === 'jokers') {
              handleDeclareClick();
            }
          }} />
        <AppMenuBar isRandomRoom={isRandomRoom} hasRoomMembership={!!playerInfo?.isJoined && playerInfo?.playerId === playerId} isHost={isHost} onSupportClick={() => setShowSupportDialog(true)} onLeaveRoomClick={() => setConfirmText(t('gameRoom.leaveRoomConfirm'))} onLogoutClick={async () => {
          intentionalExitRef.current = true;
          await handleLogoutFromRoom();
          intentionalExitRef.current = false;
        }} roomId={roomId} newMessageCount={newMessageCount}
          isFullScreen={isFullScreen} toggleFullScreen={toggleFullscreen} onInviteClick={isRandomRoom ? undefined : handleInviteClick}
          onSettingClick={handleOpenSettingDialog}
          onJoinApprovalClick={isHost && !isRandomRoom ? handleOpenJoinApproval : undefined}
          onSwapPositionsClick={canSwapPositions ? () => setShowSwapPositionsDialog(true) : undefined} onGameInfoClick={() => setShowGameInfoDialog(true)} onChatClick={() => { setShowChatDialog(true); setNewMessageCount(0); }}
          leadingTopBarControls={
              soundAccess.show && <IconButton
                size="large"
                aria-label={t('menuBar.gameSound')}
                title={t(soundAccess.locked ? 'subscription.required' : 'menuBar.gameSound')}
                aria-pressed={isSoundOn && soundAccess.allowed}
                disabled={!soundAccess.enabled}
                color="inherit"
                className="top-bar-control btn-sound"
                onClick={toggleSound}
              >
                {isSoundOn && soundAccess.allowed ? <MusicNoteIcon /> : <MusicOffIcon />}
              </IconButton>
          }
          topBarControls={roomId && playerId && <RoomVoiceChat roomId={roomId} playerId={playerId}
            hasOtherPlayer={(roomData?.players || []).filter(player => player.isJoined !== false &&
              ![PLAYER_STATE.leftRoom, PLAYER_STATE.dropped].includes(player.playerState)).length > 1} />}
        />
        <main
          className={`game-room-content${isHandVisible ? ' has-player-hand' : ''}${isResultsView ? ' is-results-view' : ''}`}
          data-game-state={gameState}
        >
          <SubscriptionWarning />
          <SubscriptionDialog open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />
          <RoomConnectionStatus state={connectionState} onRetry={() => SignalRService.retry()} />
          {gameState === GAME_STATE.readyForNextMatch && playerInfo.playerState === PLAYER_STATE.notReady && (
            <section className="next-match-panel">
              <span className="next-match-title">{t('gameRoom.playersForNextMatch')}</span>
              <div className="next-match-players">
                {allPlayers?.slice()?.sort((a, b) => {
                  if (a.playerState < b.playerState) return -1; // Ascending order
                  if (a.playerState > b.playerState) return 1;
                  return 0;
                })?.map((p, index) => (
                  <span
                    key={p.playerId}
                    className={`next-match-player${p.playerState === PLAYER_STATE.ready ? ' is-ready' : ''}${p.playerId === playerInfo?.playerId ? ' is-current' : ''}`}
                    onClick={() => {
                      if (p.playerId === playerInfo?.playerId) {
                        setShowPlayerNameDialog(true);
                      }
                    }}
                  >
                    {p.playerName}
                    {/* ({p.playerState === PLAYER_STATE.ready ? 'Ready' : 'Not Ready'}) */}
                  </span>
                ))}
              </div>
            </section>
          )}

          {(roomData && allPlayers && allPlayers.length > 0 && (gameState !== GAME_STATE.readyForNextMatch || playerInfo?.playerState === PLAYER_STATE.ready)) &&
            <section className="deck-section" data-player-count={allPlayers.length}>
              <div className="poker-deck-wrapper" data-player-count={allPlayers.length}>
              <div className={`poker-deck${lowerShowedCard && !isOpeningSequence ? ' has-lower-showed-card' : ''}`}>
                  {allPlayers?.map((player, index) => {
                    const playerTurn = isPlayerTurn(player);

                    const position = getAllPositionedPlayers(allPlayers?.length)[index];
                    const isNotPlaying = isSpectating(player?.playerState);
                    return (
                      <div
                        key={`${player.playerId}`}
                        className={`opponent seat-${position.slot}${player.playerId === playerInfo?.playerId ? ' is-current-player' : ''}${player.playerId === hostPlayer?.playerId ? ' is-host' : ''}${playerTurn ? ' is-active-player' : ''}${isNotPlaying ? ' is-inactive-player' : ''}`}
                      >
                        {(gameState === GAME_STATE.playing || gameState === GAME_STATE.winDeclared || gameState === GAME_STATE.initialHosted) &&
                          player.pickedCards?.length > 0 && <div className="picked-card-wrapper" onWheel={handleHorizontalCardScroll}>
                            {renderPickedCard(player.pickedCards?.slice()?.reverse())}
                          </div>}
                        <div className="player-panel">
                          <PlayerAvatar roomId={roomId} playerId={player.playerId} name={player.playerName}
                            data-room-player-id={player.playerId}
                            className={`avatar-player${playerTurn ? ' active' : ''}${isNotPlaying ? ' leave' : ''}`}
                            sx={{
                              cursor: `${player.isJoined !== false ? 'pointer' : 'default'}`
                            }}
                            onClick={() => {
                              if (player.playerId === playerInfo?.playerId) {
                                setShowPlayerNameDialog(true);
                              } else if (player.isJoined !== false) {
                                setInteractionPlayer(player);
                              }
                            }}
                          />
                          <div className="player-summary">
                            <div className="player-name"><ExpandableText className="player-name-text" text={player.playerName} />
                              {playerTurn && <TurnCountdown deadlineUtc={roomData?.turnDeadlineUtc} serverTimeUtc={roomData?.serverTimeUtc} />}
                            </div>
                            <div className="player-badges">
                              {player.playerId === hostPlayer?.playerId && <span className="player-badge host-badge">{t('gameRoom.host')}</span>}
                              {player.playerId === playerInfo?.playerId && <span className="player-badge self-badge">{t('gameRoom.you')}</span>}
                              {playerTurn && <span className="player-badge turn-badge">{t('gameRoom.turn')}</span>}
                              {player.isPreviousWinner && <span className="player-badge winner-badge">{t('gameRoom.winner')}</span>}
                            </div>
                            {isNotPlaying &&
                              <div className="player-state">{player.playerState === PLAYER_STATE.spectating ? t('gameRoom.waitingForNextMatch') : t('gameRoom.dropped')}</div>}
                            {player.isJoined === false && <ExpandableText className="player-state auto-play-status" text={t('roomMembership.autoPlay')} />}
                            {(!isNotPlaying && gameState === GAME_STATE.readyForNextMatch) &&
                              <div className="player-state">{player.playerState === PLAYER_STATE.ready ? t('gameRoom.ready') : t('gameRoom.notReady')}</div>}
                          </div>
                          {player.playerId !== playerInfo?.playerId && (() => {
                            const voice = player.isJoined === false ? null : voiceStatuses[player.playerId];
                            const micOn = Boolean(voice?.micOn);
                            const speakerOn = Boolean(voice?.speakerOn);
                            return <div className="player-voice-status" aria-label={t('menuBar.voiceStatus', { playerName: player.playerName })}>
                              <span className={micOn ? 'is-on' : 'is-off'} title={t(micOn ? 'menuBar.microphoneOn' : 'menuBar.microphoneMuted')}>
                                {micOn ? <MicIcon /> : <MicOffIcon />}
                              </span>
                              <span className={speakerOn ? 'is-on' : 'is-off'} title={t(speakerOn ? 'menuBar.speakerOn' : 'menuBar.speakerMuted')}>
                                {speakerOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
                              </span>
                            </div>;
                          })()}
                        </div>

                        {(gameState === GAME_STATE.playing || gameState === GAME_STATE.winDeclared || gameState === GAME_STATE.initialHosted) ?
                          <>
                            <div className="player-discarded-cards" onWheel={handleHorizontalCardScroll}>
                              <div className="pokerCard-list">
                                {(
                                  (() => {
                                    const combinedPickedCards = allPlayers.flatMap(player => player.pickedCards);
                                    const pickedCardIds = new Set(combinedPickedCards?.map(pc => pc.cardId));
                                    const filteredCards = player?.discardedCards?.filter(dc =>
                                      (currentDiscardedCard === null || dc.cardId !== currentDiscardedCard.cardId) && !pickedCardIds.has(dc.cardId));
                                    if (filteredCards?.length > 0) {
                                      return filteredCards?.slice()?.reverse()?.map((card, ind) => (
                                        <div key={`${card?.cardId}`} className="pokerCard-item">
                                          <HalfCard card={card} isShowMoneySymbol={isMoneyUpperAndLowerCard(card)} />
                                        </div>
                                      ));
                                    } else {
                                      return <HalfCard card={{}} cardStyle={{ opacity: '0' }} />;
                                    }
                                  })()
                                )}
                              </div>
                            </div>
                          </> :
                          <div className="player-discarded-cards">
                            <div className="pokerCard-list">
                              <HalfCard card={{}} cardStyle={{ opacity: '0' }} />
                            </div>
                          </div>
                        }
                      </div>
                    )
                  }
                  )}

                  <div className="desk-card-zone" aria-label="Game desk cards">
                    <div className="desk-open-zone" aria-hidden="true" />
                    <div className="desk-discard-zone">
                      <div className="desk-discard-placeholder" aria-hidden="true" />
                    </div>
                    <div className="desk-deck-zone">
                      {(gameState === GAME_STATE.initialHosted || GAME_STATE === GAME_STATE.readyForNextMatch || deckCardCount > 0) &&
                        <div className={`card deck-card ${playerInfo?.playerState === PLAYER_STATE.turnPick ? 'deck-pickable-flash' : ''}`}
                          aria-disabled={!connectionReady || isOpeningSequence} onClick={connectionReady && !isOpeningSequence ? handlePickDeckCard : undefined}>
                          {(gameState === GAME_STATE.playing && deckCardCount > 0 && deckCardCount <= 10) &&
                            <span className="deck-count">{deckCardCount}</span>
                          }
                        </div>
                      }
                      {isOpeningShuffle && <div className="desk-shuffle-animation" role="status" aria-label={t('gameRoom.shufflingCards')}>
                        <span className="shuffle-card-back" /><span className="shuffle-card-back" /><span className="shuffle-card-back" />
                      </div>}
                      {!isOpeningSequence && lowerShowedCard && <div className="lower-showed-card-clip">
                        <div className="card lowest-show-card">
                          <PokerCard card={lowerShowedCard} isShowType={true} />
                        </div>
                      </div>}
                    </div>
                  </div>

                  {isOpeningDeal && <div className="opening-deal-animation" role="status" aria-label={t('gameRoom.dealingCards')}>
                    {(() => {
                      const positioned = getAllPositionedPlayers(allPlayers.length);
                      const participants = allPlayers.map((player, index) => ({ player, index }))
                        .filter(({ player }) => !isSpectating(player.playerState));
                      const starterIndex = Math.max(0, participants.findIndex(({ player }) => player.playerState === PLAYER_STATE.turnPick));
                      const dealSequence = buildRoundRobinDealSequence(participants, starterIndex, roomData?.cardsPerPlayer || 13);
                      return dealSequence.map(({ player, index, round, sequence }) => {
                          const position = positioned[index];
                          return <span key={`${player.playerId}-${round}`} className="deal-card-back" style={{
                            '--deal-target-top': position?.top,
                            '--deal-target-left': position?.left,
                            '--deal-delay': `${sequence * DEAL_CARD_INTERVAL_MS}ms`,
                            '--deal-duration': `${DEAL_CARD_ANIMATION_MS}ms`,
                            '--deal-rotation': `${(index - (allPlayers.length - 1) / 2) * 4}deg`,
                          }} />;
                        });
                    })()}
                  </div>}

                  {!isOpeningSequence && <DeskDiscardCards roomData={roomData} playerId={playerId}
                    positions={getAllPositionedPlayers(roomData?.players?.length || 0)}
                    connectionReady={connectionReady} onPick={handlePickDiscardedCard}
                    onPickAnimationStart={() => { void playRoomSound(roomData, 'pickCardSound'); }}
                    onDiscardAnimationStart={() => { void playRoomSound(roomData, 'discardCardSound'); }} />}
                  {isPickedDeskCardMove &&
                    <div className="desk-animation-layer"><div className={`card deck-card`}
                      onAnimationStart={() => { void playRoomSound(roomData, 'pickCardSound'); }}
                      style={{
                        animation: `moveDeskCard 2s forwards`,
                        '--origin-top': 'var(--desk-discard-top)',
                        '--origin-left': 'var(--desk-deck-left)',
                        '--target-top': `${getAllPositionedPlayers(allPlayers?.length)[activePlayerIndex]?.top}`,
                        '--target-left': `${getAllPositionedPlayers(allPlayers?.length)[activePlayerIndex]?.left}`,
                      }}
                    >
                    </div></div>}
                </div>
              </div>
            </section>
          }


          {roomData && allPlayers && allPlayers.length > 0 && gameState === GAME_STATE.readyForNextMatch && playerInfo?.playerState !== PLAYER_STATE.ready &&
            <div className="declared-cards-section">
              <ShowedCardsResultRow upper={roomData.upperShowedCard} lower={roomData.lowerShowedCard} />
              {orderMatchPlayers(roomData.lastMatchResults?.length ? roomData.lastMatchResults : allPlayers, playerId)?.map((player, index) => {
                if (!player?.declaredCards?.length && !player?.discardedCards?.length) {
                  return null;
                }
                return (
                  <article key={player?.playerId || index} className={`declared-cards-wrapper player-result-card${player?.isPreviousWinner ? ' is-winner' : ''}`}>
                    <div className="player-result-title">
                      {t('gameRoom.playerCards', { playerName: player?.playerName })} {player?.isPreviousWinner ? t('gameRoom.winner') : ''}
                    </div>
                    <div className="declared-cards">
                      <div className="pokerCard-lists result-card-scroll" {...resultCardScrollProps}>
                        <div className="result-card-track">
                        {player?.declaredCards?.map((card, ind) => {
                          return (
                            <div key={ind} className="pokerCard-item"
                            >
                              <HalfCard
                                card={card}
                                isShowPickedSymbol={player?.pickedCards?.find(c => c.cardId === card.cardId)}
                              // isShowMoneySymbol={isMoneyUpperAndLowerCard(card)}
                              />
                            </div>
                          )
                        })}
                        </div>
                      </div>
                    </div>
                    {[...player?.discardedCards || [], ...player?.declaredCards || []]?.filter(c => isMoneyCard(c))?.length > 0 &&
                      <ResultCollapsibleSection className="player-result-category money-card-category" title={t('gameRoom.moneyCards')}>
                        <div className="money-cards" style={{ marginTop: '0.313rem' }}>
                          <div className="pokerCard-lists result-card-scroll" {...resultCardScrollProps}>
                            <div className="result-card-track">
                            {[...player?.discardedCards || [], ...player?.declaredCards || []]?.filter(c => isMoneyCard(c) && !player?.pickedCards?.find(pc => pc.cardId === c.cardId))?.map((card, ind) => {
                              return (
                                <div key={ind} className="pokerCard-item" style={{ marginRight: '0.125rem' }}
                                >
                                  <HalfCard
                                    card={card}
                                    isShowMoneySymbol={isMoneyUpperAndLowerCard(card)}
                                    isFullLast={true}
                                    isShowType={true}
                                  // isShowMoneySymbol={isMoneyUpperAndLowerCard(card)}
                                  />
                                </div>
                              )
                            })}
                            </div>
                          </div>
                        </div>
                      </ResultCollapsibleSection>
                    }
                    {!!player?.discardedCards?.length && <ResultCollapsibleSection className="result-discard-row player-result-category" title={t('gameRoom.discardedCards')}>
                      <div className="result-small-cards" {...resultCardScrollProps}>
                        <div className="result-card-track">
                          {player.discardedCards.map(card => <PokerCard key={card.cardId} card={card} isShowType />)}
                        </div>
                      </div>
                    </ResultCollapsibleSection>}
                  </article>
                )
              })}
              <ResultCollapsibleSection className="remaining-deck-results" title={t('gameRoom.deckCardsLeft', { count: roomData.lastMatchDeckCards?.length || 0 })}>
                <div className="result-small-cards" {...resultCardScrollProps}>
                  <div className="result-card-track">
                    {roomData.lastMatchDeckCards?.map(card => <PokerCard key={card.cardId} card={card} isShowType />)}
                  </div>
                </div>
              </ResultCollapsibleSection>
            </div>}


          {roomData && allPlayers && allPlayers.length > 0 && gameMessage &&
            <section className="game-action-section" style={{
              position: gameState === GAME_STATE.readyForNextMatch ? 'sticky' : '',
              bottom: gameState === GAME_STATE.readyForNextMatch ? '0' : ''
            }}>
              {gameMessage &&
                <div className="game-message">
                  <Typography className="msg-info"><ExpandableText text={gameMessageText} /></Typography>
                </div>
              }
              <fieldset className="game-action-buttons" disabled={!connectionReady || isOpeningSequence || (gameState === GAME_STATE.playing && turnExpired)} style={{ border: 0, padding: 0, margin: 0 }}>
                {(gameState === GAME_STATE.initialHosted && isHost) &&
                  <Button aria-label={t('gameRoom.start')} title={t('gameRoom.start')} startIcon={<PlayArrowIcon />} className="app-btn" variant="contained" color="primary" sx={{ textTransform: 'none' }} onClick={handleStartGame}>
                    <span className="action-label">{t('gameRoom.start')}</span>
                  </Button>
                }
                {gameState === GAME_STATE.readyForNextMatch && playerInfo?.playerState === PLAYER_STATE.notReady &&
                  <Button aria-label={t('gameRoom.readyBtn')} title={t('gameRoom.readyBtn')} startIcon={<CheckIcon />} className="app-btn" variant="contained" color="primary" sx={{ textTransform: 'none' }} onClick={handleReadyClick}>
                    <span className="action-label">{t('gameRoom.readyBtn')}</span>
                  </Button>
                }
                {!isRandomRoom && (gameState === GAME_STATE.initialHosted || gameState === GAME_STATE.readyForNextMatch) &&
                  <>
                    <Button aria-label={t('gameRoom.invite')} title={t('gameRoom.invite')} startIcon={<PersonAddIcon />} className="app-btn" variant="contained" color="success" sx={{ textTransform: 'none' }} onClick={handleInviteClick}>
                      <span className="action-label">{t('gameRoom.invite')}</span>
                    </Button>
                  </>
                }
                {(playerInfo?.playerState === PLAYER_STATE.pickedDeckCard || playerInfo?.playerState === PLAYER_STATE.pickedDiscardedCard) &&
                  <>
                    <DeclareWinButton onConfirm={handleDeclareClick} disabled={loading || !connectionReady || turnExpired} />
                    {playerInfo?.pickedCards?.length < 4 && !playerInfo?.mustDeclareWinner && !playerInfo?.mustDeclareWinner &&
                      <Button aria-label={t('gameRoom.discard')} title={t('gameRoom.discard')} startIcon={<DeleteOutlineIcon />} className="app-btn" variant="contained" color="success" sx={{ textTransform: 'none' }} onClick={handleDiscardClick}>
                        <span className="action-label">{t('gameRoom.discard')}</span>
                      </Button>
                    }
                  </>
                }
                {gameState === GAME_STATE.winDeclared && playerInfo?.playerState === PLAYER_STATE.declaredWin &&
                  <Button aria-label={t('gameRoom.showWinningCards')} title={t('gameRoom.showWinningCards')} startIcon={<VisibilityIcon />} className="app-btn" variant="contained" color="success" sx={{ textTransform: 'none' }} onClick={handleShowClick}>
                    <span className="action-label">{t('gameRoom.showWinningCards')}</span>
                  </Button>
                }
              </fieldset>
            </section>}

          {isHandVisible && (
            <div className={`hand-wrapper${useSingleHandRow ? ' single-row-hand' : ''}`}>
              <div className="cards-in-hand" ref={handCardsRef}>
                <div className={`pokerCard-list${useSingleHandRow ? ' single-row' : ''}`}>
                  {buildHandRows(groupedCards, useSingleHandRow).map((row, rowIndex) => (
                    <div className="hand-row" key={rowIndex} style={{ '--hand-row-units': row.units, '--hand-row-gaps': `${Math.max(0, row.segments.length - 1) * 0.28}rem` }}>
                      {row.segments.map((group, idx) => (
                        <div key={idx} className="hand-group">
                          {group.map(({ card, index }, segmentIndex) => {
                            const isRightmostCardInRow = idx === row.segments.length - 1 && segmentIndex === group.length - 1;
                            const isShowTypeAndFull = isGroupOfLastCard(card) || isRightmostCardInRow;
                            const isPickedCard = !!playerInfo?.pickedCards?.find(c => c.cardId === card.cardId);
                            return (
                              <DraggableCard
                                key={card?.cardId}
                                index={index}
                                group={card?.group}
                                card={card}
                                moveCard={moveCard}
                                selectedCardId={selectedCardId}
                                isShowPickedSymbol={isPickedCard}
                                onHandleClick={handleCardSelect}
                                isShowMoneySymbol={isMoneyUpperAndLowerCard(card) && !isPickedCard}
                                isShowType={isShowTypeAndFull}
                                isFullLast={isShowTypeAndFull || segmentIndex === group.length - 1}
                              />
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="hand-arrangement-controls">
                <Tooltip title={t('gameRoom.autoArrange')}>
                  <span className="tooltip-button-wrapper">
                    <IconButton className="btn-group-card" aria-label={t('gameRoom.autoArrange')} disabled={loading || !connectionReady || turnExpired} onClick={() => setShowAutoArrangeConfirm(true)}><SortIcon /></IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('gameRoom.groupCards')}>
                  <span className="tooltip-button-wrapper">
                    <IconButton disabled={!connectionReady} color="primary" className="btn-group-card" aria-label={t('gameRoom.groupCards')} onClick={handleGroupClick}>
                      <ViewModuleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          )
          }
          <CustomDragLayer />
        </main>
        {/* Dialog for Game Settings */}
        <Dialog fullWidth={true} open={showSettingDialog} PaperProps={{ component: 'form', onSubmit: event => {
          event.preventDefault();
          if (!loading && canEditRoomSettings) handleSaveSettings();
        } }}>
          <DialogTitle>
            {t(canEditRoomSettings ? 'gameRoom.changeRoomSettings' : 'gameSettings.viewSettings')}
            <IconButton
              aria-label="close"
              onClick={handleCancelSettings}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <GameSettingsFields deckCount={canEditRoomSettings ? settingDeckCount || 2 : roomData?.deckCount || 2}
              jokerPairs={canEditRoomSettings ? (settingJokerPairs === '' ? 2 : settingJokerPairs) : roomData?.jokerPairs ?? 0}
              cardsPerPlayer={canEditRoomSettings ? settingCardsPerPlayer : roomData?.cardsPerPlayer || 13}
              readOnly={!canEditRoomSettings}
              moneyCards={canEditRoomSettings ? settingMoneyCards : { ...DEFAULT_MONEY_CARDS, ...roomData?.moneyCardSettings }}
              maxWaitSeconds={canEditRoomSettings ? settingMaxWaitSeconds : roomData?.maxWaitSeconds ?? null}
              cardBackDesign={canEditRoomSettings ? settingCardBackDesign : roomData?.cardBackDesign || DEFAULT_CARD_BACK_DESIGN}
              roomRules={canEditRoomSettings ? settingRoomRules : Object.fromEntries(Object.keys(DEFAULT_ROOM_RULES).map(key => [key, roomData?.[key] ?? DEFAULT_ROOM_RULES[key]]))}
              showAutoApprove={false}
              onChange={settings => { setSettingDeckCount(settings.deckCount); setSettingJokerPairs(settings.jokerPairs);
                setSettingCardsPerPlayer(settings.cardsPerPlayer); setSettingMoneyCards(settings.moneyCards); setSettingMaxWaitSeconds(settings.maxWaitSeconds);
                setSettingCardBackDesign(settings.cardBackDesign); setSettingRoomRules(settings.roomRules); }} />
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <Button className="app-btn" variant="contained" color="secondary" onClick={handleCancelSettings}>{t('common.cancel')}</Button>
            {canEditRoomSettings && <Button type="submit" disabled={loading} className="app-btn" variant="contained" color="success">{t('common.change')}</Button>}
          </DialogActions>
        </Dialog>

        <Dialog fullWidth maxWidth="sm" open={showJoinApprovalDialog} PaperProps={{ component: 'form', onSubmit: event => {
          event.preventDefault();
          if (!loading) handleSaveJoinApproval();
        } }}>
          <DialogTitle>{t('gameSettings.joinApprovalTitle')}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
              <FormControlLabel sx={{ m: 0, alignItems: 'flex-start' }}
                control={<Checkbox checked={joinApprovalValue} onChange={event => setJoinApprovalValue(event.target.checked)} />}
                label={t('gameSettings.autoApproveJoinRequests')} />
              <Typography variant="body2" color="text.secondary" sx={{ pl: 4.75 }}>
                {t('gameSettings.joinApprovalHelp')}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowJoinApprovalDialog(false)}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={loading}>{t('common.change')}</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Support/Feedback */}
        <FeedbackDialog open={showSupportDialog} onClose={() => setShowSupportDialog(false)} roomId={roomId} />

        {/* Dialog for Player Name */}
        <Dialog fullWidth={true} open={showPlayerNameDialog}>
          <DialogTitle>
            {t('gameRoom.changeYourName')}
            <IconButton
              aria-label="close"
              onClick={() => { setTempPlayerName(''); setShowPlayerNameDialog(false); }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              autoComplete="off"
              margin="dense"
              label={t('gameRoom.playerName')}
              type="text"
              fullWidth
              variant="standard"
              value={tempPlayerName}
              onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing && !loading) { event.preventDefault(); handleChangePlayerName(); } }}
              onChange={(e) => setTempPlayerName(e.target.value)}
              error={isNullOrEmptyString(tempPlayerName) || tempPlayerName.length > maxPlayerNameLength}
              helperText={
                isNullOrEmptyString(tempPlayerName)
                  ? t('gameRoom.playerNameRequired')
                  : tempPlayerName.length > maxPlayerNameLength
                    ? t('gameRoom.playerNameMaxLength', { max: maxPlayerNameLength })
                    : ""
              }
            />
          </DialogContent>
          <DialogActions>
            <Button className="app-btn" variant="contained"
              onClick={async () => {
                if (isNullOrEmptyString(tempPlayerName) || tempPlayerName.length > maxPlayerNameLength) {
                  return;
                }
                await handleChangePlayerName();
              }}
            >
              {t('common.change')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Swap Positions */}
        <Dialog fullWidth={true} open={showSwapPositionsDialog && canSwapPositions}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="swapPositions-dialog-title">
            {t('gameRoom.swapPositions')}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => { setShowSwapPositionsDialog(false) }}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <table className="table table-bordered border-stripe tbl-game-info">
              <>
                <thead>
                  <tr>
                    <td className='title' colSpan={2}>
                      {allPlayers && allPlayers?.filter(p => p.playerId !== playerId)?.length > 0 ? t('gameRoom.opponentPlayers') : t('gameRoom.noOpponentPlayer')}
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {allPlayers && allPlayers?.filter(p => p.playerId !== playerId)?.length > 0 &&
                    (
                      allPlayers?.filter(p => p.playerId !== playerId).map(player => {
                        const isRequested = !!(swapRequests?.find(sr => sr.targetPlayerId === player.playerId));
                        const isRespone = !!(swapRequests?.find(sr => sr.requestPlayerId === player.playerId));
                        return (
                          <tr key={player?.playerId}>
                            <td className="column-title">
                              {player.playerName}
                            </td>
                            {isRequested ?
                              (<td>
                                <button onClick={() => handleRequestSwap(player.playerId)} type='button' className='btn-remove'>{t('common.cancel')}</button>
                              </td>) : isRespone ? (
                                <td>
                                  <button onClick={() => handleResponseSwap(player.playerId, true)} type='button' className='btn-remove mb-1 me-1'>{t('common.accept')}</button>
                                  <button onClick={() => handleResponseSwap(player.playerId)} type='button' className='btn-remove'>{t('common.decline')}</button>
                                </td>
                              ) : (
                                <td>
                                  <button onClick={() => handleRequestSwap(player.playerId)} type='button' className='btn-remove'>{t('common.request')}</button>
                                </td>
                              )

                            }
                          </tr>
                        )
                      })
                    )
                  }
                </tbody>
              </>
            </table>

          </DialogContent>
        </Dialog>

      </div>
    );
};

export default function GameRoomRoute() {
  const { roomId, playerId } = useParams();
  return <GameRoom key={`${roomId}:${playerId}`} />;
}
